import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Autocomplete,
  AutocompleteItem,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Card,
  CardBody,
  Divider,
  Checkbox,
  Chip,
  Tooltip,
  Spinner,
} from '@nextui-org/react';
import { SettlementDetail } from 'common/src/api/excel-process';
import {
  ExtractedSettlementCostLine,
  OFFICIAL_COST_CATEGORIES,
  OFFICIAL_COST_CATEGORY_LABELS,
  OfficialCostCategory,
  ResolvedSettlementCostLine,
} from 'common/src/api/receiver-costs';
import {
  SettlementDetails,
  ShipTrip,
  Market,
  Ship,
  Species,
  MarketComment,
  ExporterReceiver,
} from 'common/src/utils/types';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { hasSettlementKilosMismatch } from 'common/src/utils/settlement-detail-validation';
import { getShipTrips } from 'common/src/api/ship_trips';
import { Icon } from '@iconify/react';
import { Alert, LoadingState, useConfirm } from '../ui';
import { getPrimaryMarkets } from 'common/src/api/markets';
import { getShips } from 'common/src/api/ships';
import useUserStore from '../../../../common/src/stores/useUserStore';
import useDataStore from '../../../../common/src/stores/useDataStore';
import { subDays } from 'date-fns';
import TransportTypeSelector from '../common/TransportTypeSelector';
import { formatDate } from '../../utils/functions';
import { checkSettlementExists } from 'common/src/api/settlements';
import { SEASONS, VALID_FORMATS } from 'common/src/utils/definitions';
import {
  calculateSeasonFromDate,
  isSeasonDateAligned,
  getSeasonMismatchMessage,
} from '../../utils/seasonUtils';
import useFruticStore from '../../store/useFruticStore';
import { ShipmentBox } from 'common/src/utils/types';
import { getFruticReceiverMapping } from 'common/src/api/frutic';
import {
  resolveSettlementPreviewReceiver,
  type PreviewReceiverMapping,
} from 'common/src/utils/settlement-preview-receiver';
import {
  getShipmentBoxesForContainer,
  getShipmentBoxesForReference,
} from 'common/src/api/shipments';
import { supabase } from 'common/src/supabase';
import { getCurrencies, Currency } from 'common/src/api/currencies';
import {
  listMarketComments,
  getMarketCommentAttachmentSignedUrl,
} from 'common/src/api/market_comments';
import useWarningsStore from '../../store/warningsStore';
import useExporterLabelsStore from '../../store/useExporterLabelsStore';
import { getReceiversByExporter } from 'common/src/api/exporterReceivers';
import {
  markSettlementDocumentFailed,
  uploadOriginalSettlementDocument,
} from '../../services/settlementDocumentService';
import ReceiverCostClassificationTable from './receiver-cost-classification-table';
import ExporterCostSummary from './exporter-cost-summary';
import CostStandardToggle from './cost-standard-toggle';
import { CostStandard } from '../../hooks/useExporterCostView';
import { useReceiverExporterCosts } from '../../hooks/useReceiverExporterCosts';
import { isExporterCostViewSeason } from 'common/src/api/exporter-cost-categories';
import SettlementSourcePreview from './settlement-source-preview';
import { AddClientModal } from '../settings/AddClientModal';
import { InboxClaim, inboxRequest } from 'common/src/api/settlement-inbox';
import {
  getExporterDivisions,
  type ExporterDivision,
} from 'common/src/api/exporter-divisions';
import {
  buildSettlementQuadratureProductKey,
  buildShipmentQuadratureProductKey,
  getQuadraturePolicy,
} from 'common/src/utils/quadrature';

interface SettlementFormData {
  exporter_receiver_id?: number | null;
  reference_number: string;
  receiver: string;
  legacy_receiver?: string | null;
  business_type: string;
  sale_order: string;
  container_number: string;
  date_sold: string;
  market_id: string;
  comparable_market_id: string;
  region: string;
  local_currency: string;
  original_incoterm: string;
  ship_trip_id?: number;
  total_revenue_local: number;
  total_tax_local: number;
  total_commission_local: number;
  total_other_costs_local: number;
  total_repacking_costs_local: number;
  total_freight_usd: number;
  exchange_rate: number;
  settlement_details: Partial<SettlementDetails & { box_type: string }>[];
  season: string;
  species_id: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  excelData: SettlementDetail[] | null;
  onSuccess: () => void;
  selectedSpecies: Species | null;
  defaultSeason?: string;
  isManualUpload?: boolean;
  receiverNameSource?: 'shipment' | 'settlement';
  // When uploading via FruitMarkets, allow overriding the display receiver name
  preferredReceiverName?: string;
  // Original file processed by FruitMarkets. Manual uploads do not have one.
  sourceFile?: File | null;
  inboxClaim?: InboxClaim;
  costLines?: ExtractedSettlementCostLine[];
  /** Division already chosen upstream (e.g. shipment lookup). */
  initialDivisionId?: number | null;
}

type ShipmentBoxWithQuadrature = ShipmentBox & {
  labeled_variety?: string | null;
  unit_weight?: number | string | null;
};

type ValidBoxFormat = (typeof VALID_FORMATS)[number];

const SALE_DATE_TRIP_WINDOW_DAYS = 30;
const EMPTY_COST_LINES: ExtractedSettlementCostLine[] = [];
const ADD_CLIENT_OPTION_KEY = '__add_client__';
const ADD_CLIENT_OPTION_LABEL = 'Añadir nuevo cliente';
const mergeExporterReceivers = (...lists: ExporterReceiver[][]) =>
  Array.from(
    new Map(lists.flat().map((receiver) => [receiver.id, receiver])).values()
  ).sort((a, b) => a.name.localeCompare(b.name));
const filterClientOption = (textValue: string, inputValue: string) => {
  const normalize = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase();
  return (
    textValue === ADD_CLIENT_OPTION_LABEL ||
    normalize(textValue).includes(normalize(inputValue))
  );
};
const normalizeCostCurrency = (value: string | null | undefined) => {
  const normalized = String(value || '')
    .trim()
    .toUpperCase();
  return normalized === 'CNY' ? 'RMB' : normalized;
};

const UploadSettlementDialog: React.FC<Props> = ({
  isManualUpload,
  receiverNameSource = 'settlement',
  isOpen,
  onClose,
  excelData,
  onSuccess,
  selectedSpecies,
  defaultSeason,
  preferredReceiverName,
  sourceFile,
  inboxClaim,
  costLines = EMPTY_COST_LINES,
  initialDivisionId = null,
}) => {
  // console.log('excelData', excelData);
  const { session, userProfile } = useUserStore();
  const { species } = useDataStore();
  const clearWarnings = useWarningsStore((state) => state.clearWarnings);
  const [fetchedMappings, setFetchedMappings] = useState(false);
  const confirm = useConfirm();
  const { mappedFormats, fetchMappings, saveMapping } = useFruticStore();
  const buildInitialFormData = useCallback(
    (): SettlementFormData => ({
      reference_number: '',
      exporter_receiver_id: null,
      receiver: '',
      legacy_receiver: '',
      business_type: '',
      sale_order: '',
      container_number: '',
      date_sold: '',
      market_id: '',
      comparable_market_id: '',
      region: '',
      local_currency: '',
      original_incoterm: '',
      ship_trip_id: undefined,
      total_revenue_local: 0,
      total_tax_local: 0,
      total_commission_local: 0,
      total_other_costs_local: 0,
      total_repacking_costs_local: 0,
      total_freight_usd: 0,
      exchange_rate: 0,
      settlement_details: [],
      season: defaultSeason || '',
      species_id: selectedSpecies?.id || 1,
    }),
    [defaultSeason, selectedSpecies?.id]
  );
  const [formData, setFormData] =
    useState<SettlementFormData>(buildInitialFormData);
  const [resolvedCostLines, setResolvedCostLines] = useState<
    ResolvedSettlementCostLine[]
  >([]);
  const [costStandard, setCostStandard] = useState<CostStandard>('fm');
  const receiverExporterCosts = useReceiverExporterCosts(
    userProfile?.exporter_id,
    formData.exporter_receiver_id
  );
  const exporterCostsAvailable =
    receiverExporterCosts.available &&
    isExporterCostViewSeason(formData.season);
  const showExporterCosts =
    exporterCostsAvailable && costStandard === 'exporter';
  // Cost-line editor narrowed to one exporter category (null = Sin clasificar).
  const [exporterCostEditorFilter, setExporterCostEditorFilter] = useState<
    number | null | undefined
  >(undefined);
  const [reviewCostLines, setReviewCostLines] =
    useState<ExtractedSettlementCostLine[]>(costLines);
  const [costMappingsReady, setCostMappingsReady] = useState(
    reviewCostLines.length === 0
  );
  const [costEditorCategory, setCostEditorCategory] = useState<
    OfficialCostCategory | 'all' | null
  >(null);
  useEffect(() => {
    if (costEditorCategory === null) setExporterCostEditorFilter(undefined);
  }, [costEditorCategory]);
  const [isSourcePreviewOpen, setIsSourcePreviewOpen] = useState(
    Boolean(sourceFile)
  );
  const [loading, setLoading] = useState(false);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [ships, setShips] = useState<Ship[]>([]);
  const [shipTrips, setShipTrips] = useState<ShipTrip[]>([]);
  const [exporterReceivers, setExporterReceivers] = useState<
    ExporterReceiver[]
  >([]);
  const [receiverMappings, setReceiverMappings] = useState<
    PreviewReceiverMapping[]
  >([]);
  const [receiverLookupExporterId, setReceiverLookupExporterId] = useState<
    number | null
  >(null);
  const [addClientScope, setAddClientScope] = useState<{
    exporterId: number;
    version: number;
  } | null>(null);
  const clientCreationVersion = useRef(0);

  useEffect(() => {
    setAddClientScope(null);
    return () => {
      clientCreationVersion.current += 1;
    };
  }, [isOpen, userProfile?.exporter_id]);

  // Multi-division exporters may pin the division; by default the settlement
  // follows the division of the shipment with the same reference.
  const [divisions, setDivisions] = useState<ExporterDivision[]>([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState('');
  useEffect(() => {
    setSelectedDivisionId(initialDivisionId ? String(initialDivisionId) : '');
    if (!isOpen || !userProfile?.exporter_id) return;
    getExporterDivisions(userProfile.exporter_id)
      .then(setDivisions)
      .catch((error) => console.error('Error loading divisions:', error));
  }, [isOpen, userProfile?.exporter_id, initialDivisionId]);

  const handleOpenAddClient = useCallback(() => {
    if (!isOpen || !userProfile?.exporter_id) return;
    setAddClientScope({
      exporterId: userProfile.exporter_id,
      version: ++clientCreationVersion.current,
    });
  }, [isOpen, userProfile?.exporter_id]);

  const handleCloseAddClient = useCallback(() => {
    if (addClientScope?.version !== clientCreationVersion.current) return;
    clientCreationVersion.current += 1;
    setAddClientScope(null);
  }, [addClientScope]);

  const handleClientAdded = useCallback(
    (receiver: ExporterReceiver) => {
      if (
        !addClientScope ||
        addClientScope.version !== clientCreationVersion.current ||
        useUserStore.getState().userProfile?.exporter_id !==
          addClientScope.exporterId ||
        receiver.exporter_id !== addClientScope.exporterId
      )
        return;
      setExporterReceivers((current) =>
        mergeExporterReceivers(current, [receiver])
      );
      setFormData((current) => ({
        ...current,
        exporter_receiver_id: receiver.id,
        receiver: receiver.name,
        legacy_receiver: current.legacy_receiver || current.receiver,
      }));
    },
    [addClientScope]
  );
  const [selectedShipTripId, setSelectedShipTripId] = useState<string>('');
  const [editingCell, setEditingCell] = useState<{
    detailId: number;
    field: string;
    value: string;
  } | null>(null);
  const [editingCostField, setEditingCostField] = useState<{
    field:
      | 'commission'
      | 'tax'
      | 'freight'
      | 'otros'
      | 'repacking'
      | 'exchange_rate';
    value: string;
  } | null>(null);
  const [isAddingNewDetail, setIsAddingNewDetail] = useState(false);
  const [newDetailData, setNewDetailData] = useState<
    Partial<SettlementDetails & { box_type: string }>
  >({});
  const [suggestedMarketName, setSuggestedMarketName] = useState<string>('');
  const [transportType, setTransportType] = useState<string>('');
  const [selectedShipId, setSelectedShipId] = useState<string>('');
  const [suggestedShipName, setSuggestedShipName] = useState<string>('');
  const [mainMessage, setMainMessage] = useState<string>('');
  const [checkFormats, setCheckFormats] = useState(false);

  // Copy functionality state
  const [copyMode, setCopyMode] = useState<{
    field: 'caliber' | 'color' | 'label' | 'variety' | 'format' | 'category';
    value: string;
  } | null>(null);
  const [selectedRowsForCopy, setSelectedRowsForCopy] = useState<Set<number>>(
    new Set()
  );

  // State for manual trip creation (for A and T ships)
  const [manualTripData, setManualTripData] = useState({
    embarque_market_id: '',
    fecha_embarque: '',
    destino_market_id: '',
    fecha_llegada: '',
  });

  // Suggestions from shipment lookups
  const [suggestedContainerNumber, setSuggestedContainerNumber] =
    useState<string>('');
  const [suggestedReferenceNumber, setSuggestedReferenceNumber] =
    useState<string>('');

  // Add new states after existing states
  const [shipmentAggregates, setShipmentAggregates] = useState<
    Map<string, number>
  >(new Map());
  const [shipmentLabels, setShipmentLabels] = useState<Map<string, string>>(
    new Map()
  );
  const [validationShipmentBoxes, setValidationShipmentBoxes] = useState<
    ShipmentBoxWithQuadrature[]
  >([]);
  const [inlineFormatSelections, setInlineFormatSelections] = useState<
    Record<string, string>
  >({});
  const [localFormatOverrides, setLocalFormatOverrides] = useState<
    Record<string, ValidBoxFormat>
  >({});
  const [savingInlineFormatCode, setSavingInlineFormatCode] = useState<
    string | null
  >(null);
  const [fetchShipmentError, setFetchShipmentError] = useState<string>('');
  const [isFetchingShipment, setIsFetchingShipment] = useState<boolean>(false);
  const [isFetchingContainer, setIsFetchingContainer] =
    useState<boolean>(false);
  const [mappedFruticCodes, setMappedFruticCodes] = useState<Set<string>>(
    new Set()
  );
  const [unmappedFormatCount, setUnmappedFormatCount] = useState<number>(0);
  const [marketComments, setMarketComments] = useState<MarketComment[]>([]);
  const [isLoadingMarketComments, setIsLoadingMarketComments] =
    useState<boolean>(false);
  const [marketCommentsError, setMarketCommentsError] = useState<string>('');
  const [showMarketComments, setShowMarketComments] = useState<boolean>(false);
  const [marketCommentImageUrls, setMarketCommentImageUrls] = useState<
    Record<number, string>
  >({});
  const [unknownLabels, setUnknownLabels] = useState<string[]>([]);
  const [unknownVarieties, setUnknownVarieties] = useState<string[]>([]);
  const marketCommentsCountRef = useRef<number>(0);
  const shipmentLookupVersion = useRef(0);
  useEffect(() => {
    shipmentLookupVersion.current += 1;
    setIsFetchingShipment(false);
    setIsFetchingContainer(false);
  }, [
    isOpen,
    formData.reference_number,
    formData.container_number,
    formData.season,
    formData.species_id,
    userProfile?.exporter_id,
  ]);

  const isSearchingShipments = isFetchingShipment || isFetchingContainer;
  const [shipmentTripSuggestion, setShipmentTripSuggestion] = useState<{
    ship_name: string;
    ship_etd: string;
    ship_eta: string;
    ship_destination_port?: string | null;
  } | null>(null);
  const canFetchMarketComments = Boolean(
    (formData.reference_number || formData.container_number) &&
      formData.species_id &&
      formData.season
  );
  const {
    labels: officialLabels,
    fetchLabels,
    loading: isLoadingLabels,
  } = useExporterLabelsStore();

  /** Adjust the last detail so that sum(details[field]) === headerTotal */
  const applyRemainderCorrection = (
    details: any[],
    field: string,
    headerTotal: number
  ) => {
    if (details.length === 0) return;
    const sum = details.reduce(
      (acc: number, d: any) => acc + (d[field] || 0),
      0
    );
    const remainder = headerTotal - sum;
    if (Math.abs(remainder) > 0.000001) {
      details[details.length - 1][field] =
        (details[details.length - 1][field] || 0) + remainder;
    }
  };

  /** Recalculate usd_total_fob on a detail after cost corrections */
  const recalcFob = (detail: any, exchangeRate: number) => {
    detail.usd_total_fob =
      ((detail.local_total_price || 0) -
        (detail.local_total_tax || 0) -
        (detail.local_total_commision || 0) -
        (detail.local_other_costs || 0) -
        (detail.local_repacking_costs || 0)) /
        (exchangeRate || 1) -
      (detail.usd_freight || 0);
  };

  const normalizeMatchValue = (value: string | null | undefined) =>
    (value || '')
      .trim()
      .toLowerCase()
      .replace(/[-/\s]+/g, '');

  const normalizeReceiverName = (value: string | null | undefined) =>
    (value || '').trim().toUpperCase();

  const normalizeToken = (value: string | number | null | undefined) =>
    (value ?? '').toString().trim().toUpperCase();

  const displayToken = (value: string | number | null | undefined) => {
    const trimmed = (value ?? '').toString().trim();
    return trimmed.length > 0 ? trimmed : 'N/A';
  };

  const displayUnitWeight = (value: string | number | null | undefined) => {
    const trimmed = (value ?? '').toString().trim();
    return trimmed.length > 0 ? `${trimmed} KG` : 'N/A';
  };

  const buildSettlementProductLabel = (detail: {
    variety?: string | null;
    caliber?: string | null;
    color?: string | null;
    format?: string | null;
    label?: string | null;
    unit_weight?: number | string | null;
    category?: string | null;
  }) =>
    [
      displayToken(detail.variety),
      displayToken(detail.caliber),
      displayToken(detail.color),
      displayToken(detail.format),
      displayToken(detail.label),
      displayUnitWeight(detail.unit_weight),
      getQuadraturePolicy({ season: formData.season }).includeCategory
        ? displayToken(detail.category)
        : '',
    ]
      .filter(Boolean)
      .join('-');

  const buildShipmentProductLabel = (box: {
    labeled_variety?: string | null;
    variety?: string | null;
    caliber?: string | null;
    color?: string | null;
    box_format?: string | null;
    box_label?: string | null;
    unit_weight?: number | string | null;
    box_weight?: number | string | null;
    category?: string | null;
  }) =>
    [
      displayToken(box.labeled_variety ?? box.variety),
      displayToken(box.caliber),
      displayToken(box.color),
      displayToken(box.box_format),
      displayToken(box.box_label),
      displayUnitWeight(box.unit_weight ?? box.box_weight),
      getQuadraturePolicy({ season: formData.season }).includeCategory
        ? displayToken(box.category)
        : '',
    ]
      .filter(Boolean)
      .join('-');

  const normalizeBoxCode = (value: string | null | undefined) =>
    (value || '').trim().toUpperCase();

  const isValidBoxFormat = (
    value: string | null | undefined
  ): value is ValidBoxFormat =>
    VALID_FORMATS.includes((value || '') as ValidBoxFormat);

  const getMappedCherryFormat = (boxType: string | null | undefined) => {
    const normalizedCode = normalizeBoxCode(boxType);
    if (!normalizedCode) return undefined;

    const localFormat = localFormatOverrides[normalizedCode];
    if (localFormat) return localFormat;

    return mappedFormats.find(
      (mapping) => normalizeBoxCode(mapping.frutic_code) === normalizedCode
    )?.cherry_box_format;
  };

  const getBoxFormatOrMissing = (box: {
    box_type?: string | null;
    box_format?: string | null;
  }) => {
    const mappedFormat = getMappedCherryFormat(box.box_type);
    if (mappedFormat) return mappedFormat;

    const normalizedFormat = normalizeToken(box.box_format);
    if (isValidBoxFormat(normalizedFormat)) return normalizedFormat;

    return 'NO ENCONTRADO';
  };

  const applyShipmentMappings = (boxes: ShipmentBoxWithQuadrature[]) =>
    boxes.map((box) => {
      const box_format = getBoxFormatOrMissing(box);
      const unit_weight = box.unit_weight ?? box.box_weight;

      return {
        ...box,
        box_format,
        unit_weight,
      };
    });

  const buildShipmentAggregates = (boxes: ShipmentBoxWithQuadrature[]) => {
    const aggregates = new Map<string, number>();
    const labels = new Map<string, string>();
    const mappedBoxes = applyShipmentMappings(boxes);

    mappedBoxes.forEach((box) => {
      const key = buildShipmentQuadratureProductKey(box, {
        season: formData.season,
        speciesId: formData.species_id,
      });
      const qty = aggregates.get(key) || 0;
      aggregates.set(key, qty + (box.quantity || 0));
      if (!labels.has(key)) {
        labels.set(key, buildShipmentProductLabel(box));
      }
    });

    return { aggregates, labels, mappedBoxes };
  };

  const missingFormatCodes = useMemo(() => {
    const missingCodes = new Set<string>();

    formData.settlement_details.forEach((detail) => {
      const code = normalizeBoxCode(detail.box_type);
      if (!code) return;
      if (!isValidBoxFormat(detail.format)) {
        missingCodes.add(code);
      }
    });

    validationShipmentBoxes.forEach((box) => {
      const code = normalizeBoxCode(box.box_type);
      if (!code) return;
      const mappedFormat = getMappedCherryFormat(code);
      if (!mappedFormat && !isValidBoxFormat(normalizeToken(box.box_format))) {
        missingCodes.add(code);
      }
    });

    return Array.from(missingCodes).sort((a, b) => a.localeCompare(b));
  }, [
    formData.settlement_details,
    validationShipmentBoxes,
    mappedFormats,
    localFormatOverrides,
  ]);

  const handleInlineFormatAssignment = useCallback(
    async (code: string) => {
      const selectedFormat = inlineFormatSelections[code];
      if (!isValidBoxFormat(selectedFormat)) {
        toast.error('Selecciona un formato válido para el código de embalaje.');
        return;
      }

      if (!userProfile?.exporter_id) {
        toast.error('No se pudo identificar el exportador para guardar.');
        return;
      }

      setSavingInlineFormatCode(code);
      try {
        await saveMapping(code, selectedFormat, userProfile.exporter_id);
        setLocalFormatOverrides((prev) => ({
          ...prev,
          [code]: selectedFormat,
        }));
        setMappedFruticCodes((prev) => {
          const next = new Set(prev);
          next.add(code);
          return next;
        });
        setFormData((prev) => ({
          ...prev,
          settlement_details: prev.settlement_details.map((detail) =>
            normalizeBoxCode(detail.box_type) === code
              ? { ...detail, format: selectedFormat }
              : detail
          ),
        }));
        setInlineFormatSelections((prev) => {
          const next = { ...prev };
          delete next[code];
          return next;
        });
        toast.success(`Formato ${selectedFormat} asignado a ${code}.`);
      } finally {
        setSavingInlineFormatCode(null);
      }
    },
    [inlineFormatSelections, saveMapping, userProfile?.exporter_id]
  );

  const normalizeLabel = (value: string | null | undefined) =>
    (value || '').trim().toUpperCase();

  const normalizeVariety = (value: string | null | undefined) =>
    (value || '').trim().toUpperCase();

  const buildContainerSearchValue = (value: string | null | undefined) => {
    if (!value) return undefined;
    return value.trim().replace(/[-/\s]+/g, '%');
  };

  const formatMarketValue = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') return '—';
    return String(value);
  };

  useEffect(() => {
    setReviewCostLines(costLines);
    setResolvedCostLines([]);
    setCostMappingsReady(costLines.length === 0);
    setCostEditorCategory(null);
  }, [costLines]);

  useEffect(() => {
    setIsSourcePreviewOpen(Boolean(isOpen && sourceFile));
  }, [isOpen, sourceFile]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!canFetchMarketComments) {
        setMarketComments([]);
        setMarketCommentsError('');
        return;
      }
      setIsLoadingMarketComments(true);
      setMarketCommentsError('');
      try {
        const baseParams = {
          species_id: formData.species_id,
          season: formData.season,
          limit: 50,
        };
        const reference = normalizeMatchValue(formData.reference_number);
        const container = normalizeMatchValue(formData.container_number);

        let data: MarketComment[] = [];
        if (reference && container) {
          const [byRef, byContainer] = await Promise.all([
            listMarketComments({
              ...baseParams,
              exporter_ref: formData.reference_number || undefined,
            }),
            listMarketComments({
              ...baseParams,
              container_number:
                buildContainerSearchValue(formData.container_number) ||
                undefined,
            }),
          ]);
          const merged = new Map<number, MarketComment>();
          [...byRef, ...byContainer].forEach((comment) => {
            merged.set(comment.id, comment);
          });
          data = Array.from(merged.values());
        } else {
          data = await listMarketComments({
            ...baseParams,
            exporter_ref: formData.reference_number || undefined,
            container_number:
              buildContainerSearchValue(formData.container_number) || undefined,
          });
        }
        if (!cancelled) setMarketComments(data || []);
      } catch (error) {
        if (!cancelled) {
          setMarketCommentsError(
            'No se pudieron cargar los comentarios de mercado.'
          );
        }
      } finally {
        if (!cancelled) setIsLoadingMarketComments(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    canFetchMarketComments,
    formData.reference_number,
    formData.container_number,
    formData.species_id,
    formData.season,
  ]);

  useEffect(() => {
    if (marketComments.length === 0) {
      setShowMarketComments(false);
    } else if (marketCommentsCountRef.current === 0) {
      setShowMarketComments(true);
    }
    marketCommentsCountRef.current = marketComments.length;
  }, [marketComments.length]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const attachments = marketComments.flatMap(
        (comment) => comment.attachments || []
      );
      const pending = attachments.filter(
        (attachment) =>
          attachment.is_image &&
          !attachment.deleted_at &&
          !marketCommentImageUrls[attachment.id]
      );

      if (pending.length === 0) return;

      try {
        const entries = await Promise.all(
          pending.map(async (attachment) => {
            const url = await getMarketCommentAttachmentSignedUrl(
              attachment.bucket,
              attachment.path,
              300
            );
            return [attachment.id, url] as const;
          })
        );
        if (!cancelled) {
          setMarketCommentImageUrls((prev) => {
            const next = { ...prev };
            entries.forEach(([id, url]) => {
              next[id] = url;
            });
            return next;
          });
        }
      } catch {
        // Non-blocking: attachments can still be opened on demand.
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [marketComments, marketCommentImageUrls]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!isOpen || !userProfile?.exporter_id) {
        setMappedFruticCodes(new Set());
        return;
      }

      const { data, error } = await supabase
        .from('frutic_box_to_format')
        .select('frutic_code')
        .eq('exporter_id', userProfile.exporter_id);

      if (error) {
        console.error('Error fetching frutic mappings:', error);
        if (!cancelled) setMappedFruticCodes(new Set());
        return;
      }

      const codes = new Set(
        (data || [])
          .map((row: { frutic_code: string | null }) =>
            (row.frutic_code || '').trim().toUpperCase()
          )
          .filter((code) => code.length > 0)
      );

      if (!cancelled) setMappedFruticCodes(codes);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [isOpen, userProfile?.exporter_id]);

  useEffect(() => {
    if (!isOpen) return;
    if (mappedFruticCodes.size === 0) {
      setCheckFormats(false);
      setUnmappedFormatCount(0);
      return;
    }

    const missing = new Set<string>();
    formData.settlement_details.forEach((detail) => {
      const code = (detail.box_type || '').trim().toUpperCase();
      if (!code) return;
      if (!mappedFruticCodes.has(code)) {
        missing.add(code);
      }
    });

    const missingCount = missing.size;
    setCheckFormats(missingCount > 0);
    setUnmappedFormatCount(missingCount);
  }, [isOpen, formData.settlement_details, mappedFruticCodes]);

  useEffect(() => {
    if (!isOpen) return;
    setCheckFormats(missingFormatCodes.length > 0);
    setUnmappedFormatCount(missingFormatCodes.length);
  }, [isOpen, missingFormatCodes]);

  useEffect(() => {
    if (validationShipmentBoxes.length === 0) return;

    const { aggregates, labels, mappedBoxes } = buildShipmentAggregates(
      validationShipmentBoxes
    );
    setShipmentAggregates(aggregates);
    setShipmentLabels(labels);
    setShipmentTripSuggestion(buildTripSuggestionFromShipments(mappedBoxes));
  }, [
    validationShipmentBoxes,
    mappedFormats,
    localFormatOverrides,
    formData.season,
    formData.species_id,
  ]);

  useEffect(() => {
    if (!isOpen) {
      clearWarnings();
    }
  }, [isOpen, clearWarnings]);

  useEffect(() => {
    if (
      !isOpen ||
      !userProfile?.exporter_id ||
      !formData.season ||
      !formData.species_id
    ) {
      return;
    }

    fetchLabels(
      userProfile.exporter_id,
      formData.species_id,
      formData.season
    ).catch(() => undefined);
  }, [
    fetchLabels,
    formData.season,
    formData.species_id,
    isOpen,
    userProfile?.exporter_id,
  ]);

  const allowedLabels = useMemo(() => {
    if (!userProfile?.exporter_id || !formData.season || !formData.species_id) {
      return [];
    }
    const normalized = Array.from(
      new Set(
        officialLabels
          .map((item) => normalizeLabel(item.label))
          .filter((label) => label.length > 0)
      )
    );
    return normalized.sort((a, b) => a.localeCompare(b));
  }, [
    formData.season,
    formData.species_id,
    officialLabels,
    userProfile?.exporter_id,
  ]);

  const allowedVarieties = useMemo(() => {
    const selected = species.find((item) => item.id === formData.species_id);
    const normalized = Array.from(
      new Set(
        (selected?.all_varieties || [])
          .map((item) => normalizeVariety(item))
          .filter((variety) => variety.length > 0)
      )
    );
    return normalized.sort((a, b) => a.localeCompare(b));
  }, [formData.species_id, species]);

  const selectedExporterReceiver = useMemo(
    () =>
      exporterReceivers.find(
        (receiver) => receiver.id === formData.exporter_receiver_id
      ) || null,
    [exporterReceivers, formData.exporter_receiver_id]
  );

  useEffect(() => {
    if (allowedLabels.length === 0) {
      setUnknownLabels([]);
      return;
    }

    const allowedSet = new Set(allowedLabels);
    const unknown = Array.from(
      new Set(
        formData.settlement_details
          .map((detail) => normalizeLabel(detail.label))
          .filter((label) => label.length > 0 && label !== 'N/A')
          .filter((label) => !allowedSet.has(label))
      )
    ).sort((a, b) => a.localeCompare(b));

    setUnknownLabels(unknown);
  }, [allowedLabels, formData.settlement_details]);

  useEffect(() => {
    if (allowedVarieties.length === 0) {
      setUnknownVarieties([]);
      return;
    }

    const allowedSet = new Set(allowedVarieties);
    const unknown = Array.from(
      new Set(
        formData.settlement_details
          .map((detail) => normalizeVariety(detail.variety))
          .filter((variety) => variety.length > 0 && variety !== 'N/A')
          .filter((variety) => !allowedSet.has(variety))
      )
    ).sort((a, b) => a.localeCompare(b));

    setUnknownVarieties(unknown);
  }, [allowedVarieties, formData.settlement_details]);

  // Grouping state - to store ungrouped details and track grouping status
  const [ungroupedDetails, setUngroupedDetails] = useState<
    Partial<SettlementDetails & { box_type: string }>[]
  >([]);
  const [isGrouped, setIsGrouped] = useState(false);

  // Highlight state for newly added/duplicated rows
  const [highlightedRowId, setHighlightedRowId] = useState<number | null>(null);

  // Business type options (previously used by this exporter)
  const [businessTypeOptions, setBusinessTypeOptions] = useState<string[]>([]);

  useEffect(() => {
    const fetchBusinessTypes = async () => {
      try {
        if (!userProfile?.exporter_id) return;
        const { data, error } = await supabase
          .from('settlements')
          .select('business_type')
          .eq('exporter_id', userProfile.exporter_id)
          .not('business_type', 'is', null)
          .limit(1000);

        if (error) {
          // Non-blocking: show console error, keep UI functional
          console.error('Error fetching business types:', error);
          return;
        }

        const unique = Array.from(
          new Set(
            (data || [])
              .map(
                (r: { business_type: string | null }) => r.business_type || ''
              )
              .filter((v) => v && v.trim().length > 0)
              .map((v) => v.toUpperCase())
          )
        ).sort();
        setBusinessTypeOptions(unique);
      } catch (e) {
        console.error('Unexpected error fetching business types:', e);
      }
    };

    if (isOpen) fetchBusinessTypes();
  }, [isOpen, userProfile?.exporter_id]);

  // Snapshot of freight in local currency to recompute USD after TC changes
  const [freightLocalSnapshot, setFreightLocalSnapshot] = useState<
    number | null
  >(null);
  const [hasAutoSearched, setHasAutoSearched] = useState(false);

  // Helper function to get Chile market ID
  const getChileMarketId = () => {
    const chileMarket = markets.find(
      (market) =>
        market.country.toUpperCase() === 'CHILE' ||
        market.name.toUpperCase() === 'CHILE'
    );
    return chileMarket ? chileMarket.id.toString() : '';
  };

  // Helper function to initialize manual trip data with Chile as embarque
  const initializeManualTripData = (
    fechaLlegada: string = '',
    destinoMarketId: string = ''
  ) => {
    const chileMarketId = getChileMarketId();
    setManualTripData({
      embarque_market_id: chileMarketId,
      fecha_embarque: fechaLlegada
        ? subDays(new Date(fechaLlegada), 1).toISOString().split('T')[0]
        : '',
      destino_market_id: destinoMarketId,
      fecha_llegada: fechaLlegada,
    });
  };

  // Helper function to map ship type codes to transport type names
  const getTransportTypeFromShipType = (shipType: string): string => {
    switch (shipType?.toUpperCase()) {
      case 'A':
        return 'A';
      case 'T':
        return 'T';
      case 'M':
        return 'M';
      default:
        return 'M'; // Default to maritime
    }
  };

  // Get ships filtered by transport type
  const getShipsByType = (type: string) => {
    return ships.filter(
      (ship) => getTransportTypeFromShipType(ship.type) === type
    );
  };

  const labelStatus = () => {
    if (isLoadingLabels) {
      return {
        text: 'Etiquetas: verificando...',
        className: 'bg-default-100 text-default-500',
      };
    }
    if (allowedLabels.length === 0) {
      return {
        text: 'Etiquetas: sin base para validar',
        className: 'bg-default-100 text-default-500',
      };
    }
    if (unknownLabels.length > 0) {
      return {
        text: `Etiquetas: ${unknownLabels.length} no oficiales`,
        className: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300',
      };
    }
    return {
      text: 'Etiquetas: cumplen con etiquetas oficiales',
      className:
        'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300',
    };
  };

  const labelStatusInfo = labelStatus();
  const isLabelUnknown = (value: string | null | undefined) => {
    const normalized = normalizeLabel(value);
    if (!normalized || normalized === 'N/A') return false;
    return unknownLabels.includes(normalized);
  };

  const varietyStatus = () => {
    if (allowedVarieties.length === 0) {
      return {
        text: 'Variedades: sin base para validar',
        className: 'bg-default-100 text-default-500',
      };
    }
    if (unknownVarieties.length > 0) {
      return {
        text: `Variedades: ${unknownVarieties.length} no oficiales`,
        className: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300',
      };
    }
    return {
      text: 'Variedades: cumplen con variedades oficiales',
      className:
        'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300',
    };
  };

  const varietyStatusInfo = varietyStatus();
  const isVarietyUnknown = (value: string | null | undefined) => {
    const normalized = normalizeVariety(value);
    if (!normalized || normalized === 'N/A') return false;
    return unknownVarieties.includes(normalized);
  };

  const detailValuesStatus = () => {
    const zeroQuantity =
      formData.settlement_details?.filter((d) => !d.quantity || d.quantity == 0)
        .length || 0;
    const zeroWeight =
      formData.settlement_details?.filter(
        (d) => !d.unit_weight || d.unit_weight == 0
      ).length || 0;
    const kilosMismatch = (formData.settlement_details || []).filter(
      hasSettlementKilosMismatch
    ).length;
    const total = zeroQuantity + zeroWeight + kilosMismatch;
    if (total > 0) {
      const parts: string[] = [];
      if (zeroQuantity > 0) parts.push(`${zeroQuantity} con cantidad 0`);
      if (zeroWeight > 0) parts.push(`${zeroWeight} con peso bulto 0`);
      if (kilosMismatch > 0) {
        parts.push(
          `${kilosMismatch} con kilos distintos a peso bulto × bultos`
        );
      }
      return {
        text: `Detalles: ${parts.join(', ')}`,
        className: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300',
      };
    }
    return null;
  };

  const detailValuesStatusInfo = detailValuesStatus();

  const getTripDistanceFromDate = (trip: ShipTrip, dateString: string) => {
    if (!dateString) return Number.POSITIVE_INFINITY;

    const targetDate = new Date(dateString);
    if (Number.isNaN(targetDate.getTime())) {
      return Number.POSITIVE_INFINITY;
    }

    const tripDates = [trip.etd, trip.eta]
      .filter(Boolean)
      .map((date) => new Date(date))
      .filter((date) => !Number.isNaN(date.getTime()));

    if (tripDates.length === 0) return Number.POSITIVE_INFINITY;

    return Math.min(
      ...tripDates.map((date) =>
        Math.abs(
          (date.getTime() - targetDate.getTime()) / (1000 * 60 * 60 * 24)
        )
      )
    );
  };

  // Get trips filtered by selected ship, selected season, and nearby sale date.
  // Late sales can fall outside the packing/shipping season, so keep selected
  // season trips while also surfacing trips close to the sale date.
  const getTripsByShip = (shipId: string, season: string, saleDate: string) => {
    return shipTrips
      .filter((trip) => {
        if (trip.ship.id.toString() !== shipId) return false;

        const isSelectedSeasonTrip =
          season && calculateSeasonFromDate(trip.etd) === season;
        const isAroundSaleDate =
          getTripDistanceFromDate(trip, saleDate) <= SALE_DATE_TRIP_WINDOW_DAYS;

        return isSelectedSeasonTrip || isAroundSaleDate;
      })
      .sort((a, b) => {
        const aDistance = getTripDistanceFromDate(a, saleDate);
        const bDistance = getTripDistanceFromDate(b, saleDate);

        if (aDistance !== bDistance) return aDistance - bDistance;
        return new Date(a.etd).getTime() - new Date(b.etd).getTime();
      });
  };

  // Check if current transport type requires manual trip creation
  const requiresManualTrip = () => {
    return transportType === 'A' || transportType === 'T';
  };

  const buildTripSuggestionFromShipments = (shipmentBoxes: ShipmentBox[]) => {
    if (!shipmentBoxes?.length) {
      return null;
    }
    const candidate =
      shipmentBoxes.find(
        (box) => box.ship_name && box.ship_name !== 'NO DEFINIDO'
      ) || shipmentBoxes[0];
    return {
      ship_name: candidate.ship_name || '',
      ship_etd: candidate.ship_etd || '',
      ship_eta: candidate.ship_eta || '',
      ship_destination_port: candidate.ship_destination_port || null,
    };
  };

  const formatTripSuggestionDate = (date?: string) => {
    if (!date) return 'No definido';
    try {
      return formatDate(date, 'dd MMMM yyyy');
    } catch (error) {
      console.warn('Invalid trip suggestion date', date, error);
      return 'No definido';
    }
  };

  // Transform Excel data to settlement format
  const transformExcelDataToSettlement = (
    excelData: SettlementDetail[]
  ): SettlementFormData => {
    if (!excelData || excelData.length === 0) {
      return formData;
    }

    const firstRecord = excelData[0];

    // Try to find market by name matching
    let foundMarketId = '';
    const marketNameFromExcel = firstRecord.mercado?.toUpperCase() || '';

    if (marketNameFromExcel && markets.length > 0) {
      // Try exact match first by name or id
      const exactMatch = markets.find(
        (market) =>
          market.name.toUpperCase() == marketNameFromExcel ||
          market.id.toString().toUpperCase() == marketNameFromExcel ||
          market.name.toUpperCase().replace(' ', '') == marketNameFromExcel
      );

      if (exactMatch) {
        foundMarketId = exactMatch.id.toString();
        setSuggestedMarketName(''); // Clear suggestion if exact match found
      } else {
        // Try partial match
        const partialMatch = markets.find(
          (market) =>
            market.name
              .toLowerCase()
              .includes(marketNameFromExcel.toLowerCase()) ||
            marketNameFromExcel
              .toLowerCase()
              .includes(market.name.toLowerCase())
        );

        if (partialMatch) {
          foundMarketId = partialMatch.id.toString();
          setSuggestedMarketName('');
        } else {
          setSuggestedMarketName(marketNameFromExcel);
        }
      }
    }

    // Try to find ship by name matching
    let foundShipTripId: number | undefined = undefined;
    const shipNameFromExcel = firstRecord.nave?.toUpperCase() || '';

    if (shipNameFromExcel) {
      // Look for exact ship name match first
      const exactShipMatch = ships.find((ship) =>
        new RegExp(ship.name.toLowerCase()).test(
          shipNameFromExcel.toLowerCase()
        )
      );

      if (exactShipMatch) {
        if (exactShipMatch.type === 'M') {
          // Maritime ship found - tell user and try to find trip by date
          setSuggestedShipName(`✅ Encontramos la nave ${exactShipMatch.name}`);
          setTransportType('M');
          setSelectedShipId(exactShipMatch.id.toString());

          // Try to find trip within +-7 days of fecha_venta
          if (firstRecord.fecha_venta) {
            const ventaDate = new Date(firstRecord.fecha_venta);

            const shipTripsForShip = shipTrips.filter(
              (trip) => trip.ship.id === exactShipMatch.id
            );

            let foundTrip: ShipTrip | null = null;
            for (const trip of shipTripsForShip) {
              const etaDate = new Date(trip.eta);
              const diffDays = Math.abs(
                (etaDate.getTime() - ventaDate.getTime()) /
                  (1000 * 60 * 60 * 24)
              );

              if (diffDays <= 7) {
                foundTrip = trip;
                break;
              }
            }

            if (foundTrip) {
              foundShipTripId = foundTrip.id;
              // setSuggestedShipName(
              //   `✅ Encontramos la nave ${exactShipMatch.name} ${foundTrip.market?.name ? `y el arribo a ${foundTrip.market?.name}` : ''} el ${formatDate(foundTrip.eta, 'dd MMMM yyyy')}`
              // );
              setSuggestedShipName(
                `✅ Encontramos la nave ${exactShipMatch.name}. Selecciona el viaje correspondiente.`
              );
            } else {
              // setSuggestedShipName(
              //   `✅ Encontramos la nave ${exactShipMatch.name}pero no encontramos un arribo cercano a ${formatDate(ventaDate.toISOString().split('T')[0], 'dd MMMM yyyy')} (±7 días)`
              // );
              setSuggestedShipName(
                `✅ Encontramos la nave ${exactShipMatch.name}. Selecciona el viaje correspondiente.`
              );
            }
          }
        } else {
          // Non-maritime ship found
          setTransportType(getTransportTypeFromShipType(exactShipMatch.type));
          setSelectedShipId(exactShipMatch.id.toString());
          setSuggestedShipName(
            `✅ Encontramos la nave ${exactShipMatch.name}. Completa los datos de embarque y destino.`
          );

          // Initialize manual trip data for A/T ships
          initializeManualTripData(
            firstRecord.fecha_venta?.replace(/\//g, '-') || '',
            foundMarketId
          );
        }
      } else {
        // Ship not found - tell user to select type
        setSuggestedShipName(
          `❌ La nave "${shipNameFromExcel}" no fue encontrada. Por favor selecciona el tipo de transporte y luego la nave correspondiente.`
        );
      }
    }

    // Calculate totals
    const totalRevenue = excelData.reduce(
      (sum, record) => sum + record.ingreso_moneda_origen,
      0
    );
    const totalTax = excelData.reduce(
      (sum, record) => sum + record.tax_moneda_origen,
      0
    );
    const totalCommission = excelData.reduce(
      (sum, record) => sum + record.comision_moneda_origen,
      0
    );
    const totalOtherCosts = excelData.reduce(
      (sum, record) => sum + record.costos_logisticos_moneda_origen,
      0
    );
    const totalRepackingCosts = excelData.reduce(
      (sum, record) => sum + (record.repacking_costs_moneda_origen || 0),
      0
    );
    const totalFreightUsd = excelData.reduce(
      (sum, record) => sum + record.flete_usd,
      0
    );

    const unmappedCodes = new Set<string>();

    // Transform details - NO COSTS at detail level initially, only income
    const transformedDetails: Partial<
      SettlementDetails & { category: string }
    >[] = excelData.map((record, index) => {
      let mappedFormat = '';
      let format = '';
      if (isManualUpload) {
        // console.log('mappedFormats', mappedFormats);
        // console.log('record.codigo_embalaje', record.codigo_embalaje);
        mappedFormat =
          getMappedCherryFormat(record.codigo_embalaje) || 'NO ENCONTRADO';
        // console.log('mappedFormat', mappedFormat);
        format = mappedFormat;
        if (format == 'NO ENCONTRADO') {
          if (record.codigo_embalaje) {
            unmappedCodes.add(record.codigo_embalaje);
          } else {
            unmappedCodes.add('SIN CODIGO');
          }
        }
      } else {
        format = record.formato || 'NO ENCONTRADO';
      }

      return {
        id: -(index + 1), // Temporary negative ID for new records
        pallet: record.pallet_identificador || '',
        variety: record.variedad || '',
        box_type: record.codigo_embalaje || '',
        format: format,
        caliber: record.calibre_color.trim().toUpperCase() || '',
        color: record.color?.trim().toUpperCase() || '',
        label: record.embalaje_etiqueta || '',
        category: record.categoria || '', // Will be filled manually if needed
        unit_weight: record.peso_bulto || 0,
        quantity: record.bultos_cargados || 0,
        kilos: record.kilos || 0,
        local_total_price: record.ingreso_moneda_origen || 0, // Only income, no costs
        local_currency: record.moneda || 'RMB',
        // Costs will be calculated during settlement creation
        local_total_tax: 0,
        local_total_commision: 0,
        local_other_costs: 0,
        local_repacking_costs: 0,
        exchange_rate: record.tipo_cambio || 0,
        usd_freight: 0,
        usd_total_fob: 0, // Will be calculated after cost distribution
        incoterm: record.clausula || 'FOB',
        date_sold: firstRecord.fecha_venta?.replace(/\//g, '-') || '',
        quartil_vs_market: null,
        difference_vs_market: null,
        difference_vs_market_eta: null,
        quartil_vs_market_eta: null,
      };
    });

    const hasUnmappedFormats = unmappedCodes.size > 0;
    setCheckFormats(hasUnmappedFormats);
    setUnmappedFormatCount(hasUnmappedFormats ? unmappedCodes.size : 0);

    return {
      reference_number: firstRecord.referencia || '',
      exporter_receiver_id: firstRecord.exporter_receiver_id ?? null,
      receiver: firstRecord.cliente || '',
      legacy_receiver: firstRecord.cliente || '',
      business_type: firstRecord.business_type || '',
      sale_order: firstRecord.sale_order || '',
      container_number: firstRecord.nro_contenedor || '',
      date_sold: firstRecord.fecha_venta?.replace(/\//g, '-') || '',
      market_id: foundMarketId, // Use the found market ID or empty string
      comparable_market_id: ['CNCAN', 'CNSHA'].includes(foundMarketId)
        ? foundMarketId
        : '',
      region: '',
      local_currency: firstRecord.moneda || 'RMB',
      original_incoterm:
        totalFreightUsd == 0 ? 'CIF' : firstRecord.clausula || 'FOB',
      ship_trip_id: undefined, // Let the user select the trip even if we can suggest one
      total_revenue_local: totalRevenue,
      // Settlement-level costs from Excel totals
      total_tax_local: totalTax,
      total_commission_local: totalCommission,
      total_other_costs_local: totalOtherCosts,
      total_repacking_costs_local: totalRepackingCosts,
      total_freight_usd: totalFreightUsd,
      exchange_rate: firstRecord.tipo_cambio || 0,
      settlement_details: transformedDetails,
      season:
        defaultSeason ||
        calculateSeasonFromDate(
          firstRecord.fecha_venta?.replace(/\//g, '-') || ''
        ),
      species_id: selectedSpecies?.id || 1,
    };
  };

  useEffect(() => {
    if (isManualUpload) {
      fetchMappings(
        selectedSpecies?.id,
        defaultSeason,
        session,
        null,
        userProfile
      );
      setFetchedMappings(true);
    }
  }, [userProfile, session, isManualUpload, selectedSpecies, defaultSeason]);

  useEffect(() => {
    if (excelData && isOpen && (fetchedMappings || !isManualUpload)) {
      const transformedData = transformExcelDataToSettlement(excelData);
      // If a preferred receiver name (e.g., alias used for robot) is provided,
      // overwrite receiver in preview with that name instead of the official one.
      const receiverOverridden = preferredReceiverName
        ? (preferredReceiverName || '').toUpperCase()
        : undefined;
      setFormData((prev) => {
        const nextData = receiverOverridden
          ? {
              ...transformedData,
              exporter_receiver_id: null,
              receiver: receiverOverridden,
              legacy_receiver: receiverOverridden,
            }
          : transformedData;
        return {
          ...nextData,
          business_type:
            prev.business_type && prev.business_type.trim().length > 0
              ? prev.business_type
              : nextData.business_type,
          sale_order:
            prev.sale_order && prev.sale_order.trim().length > 0
              ? prev.sale_order
              : nextData.sale_order,
          original_incoterm:
            prev.original_incoterm && prev.original_incoterm.trim().length > 0
              ? prev.original_incoterm
              : nextData.original_incoterm,
        };
      });
      // Initialize freight local snapshot to keep local freight constant when TC changes
      if (
        transformedData.local_currency?.toUpperCase() !== 'USD' &&
        (transformedData.total_freight_usd || 0) > 0 &&
        (transformedData.exchange_rate || 0) > 0
      ) {
        setFreightLocalSnapshot(
          (transformedData.total_freight_usd || 0) *
            (transformedData.exchange_rate || 1)
        );
      } else {
        setFreightLocalSnapshot(null);
      }
    }
  }, [
    excelData,
    isOpen,
    markets,
    ships,
    shipTrips,
    fetchedMappings,
    preferredReceiverName,
  ]);

  // On open, try to auto-validate against shipments: first by reference, then by container
  useEffect(() => {
    if (!isOpen || hasAutoSearched) return;
    if (!formData.season || !formData.species_id) return;

    const hasRef = !!(
      formData.reference_number && formData.reference_number.trim().length > 0
    );
    const hasCont = !!(
      formData.container_number && formData.container_number.trim().length > 0
    );
    if (!hasRef && !hasCont) {
      // Wait until either reference or container is populated
      return;
    }

    let active = true;
    const run = async () => {
      let ok = false;
      if (hasRef) {
        ok = await handleSearchShipment();
        if (!active) return;
      }
      if (!ok && hasCont) {
        ok = await handleSearchContainer();
      }
      if (active) setHasAutoSearched(true);
    };
    run();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    formData.reference_number,
    formData.container_number,
    formData.season,
    formData.species_id,
  ]);

  // Recalculate USD freight after TC updates (incremental update to avoid race conditions)
  useEffect(() => {
    if (
      reviewCostLines.length === 0 &&
      formData.local_currency?.toUpperCase() !== 'USD' &&
      freightLocalSnapshot !== null &&
      (formData.exchange_rate || 0) > 0
    ) {
      const recalculatedUsd =
        freightLocalSnapshot / (formData.exchange_rate || 1);
      const currentUsd = formData.total_freight_usd || 0;
      if (Math.abs(currentUsd - recalculatedUsd) > 1e-6) {
        setFormData((prev) => ({
          ...prev,
          total_freight_usd: recalculatedUsd,
        }));
      }
    }
  }, [formData.exchange_rate, reviewCostLines.length]);

  useEffect(() => {
    const fetchShipTrips = async () => {
      const trips = await getShipTrips();
      setShipTrips(trips || []);
    };
    fetchShipTrips();
  }, []);

  useEffect(() => {
    const fetchMarkets = async () => {
      const markets = await getPrimaryMarkets();
      setMarkets(markets || []);
    };
    fetchMarkets();
  }, []);

  useEffect(() => {
    const fetchShips = async () => {
      const ships = await getShips();
      setShips(ships || []);
    };
    fetchShips();
  }, []);

  useEffect(() => {
    setReceiverLookupExporterId(null);
    setExporterReceivers([]);
    setReceiverMappings([]);
    if (!isOpen || !userProfile?.exporter_id) return;
    const exporterId = userProfile.exporter_id;
    let cancelled = false;
    Promise.all([
      getReceiversByExporter(exporterId),
      getFruticReceiverMapping({ exporter_id: exporterId }),
    ])
      .then(([receivers, mappings]) => {
        if (cancelled) return;
        // A client can be created while this lookup is still in flight.
        setExporterReceivers((current) =>
          mergeExporterReceivers(receivers || [], current)
        );
        setReceiverMappings(mappings || []);
        setReceiverLookupExporterId(exporterId);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error fetching exporter receivers:', error);
        toast.error('Error al cargar clientes oficiales.');
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, userProfile?.exporter_id]);

  useEffect(() => {
    getCurrencies()
      .then((data) => setCurrencies(data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isOpen || receiverLookupExporterId !== userProfile?.exporter_id)
      return;
    setFormData((prev) => {
      const receiver = resolveSettlementPreviewReceiver(
        prev,
        exporterReceivers,
        receiverMappings
      );
      if (
        !receiver ||
        (prev.exporter_receiver_id === receiver.id &&
          prev.receiver === receiver.name)
      )
        return prev;
      return {
        ...prev,
        exporter_receiver_id: receiver.id,
        receiver: receiver.name,
        legacy_receiver: prev.legacy_receiver || prev.receiver,
      };
    });
  }, [
    isOpen,
    userProfile?.exporter_id,
    receiverLookupExporterId,
    exporterReceivers,
    receiverMappings,
    formData.exporter_receiver_id,
    formData.receiver,
  ]);

  // Check settlement existence when season, species, or reference changes
  useEffect(() => {
    // console.log('formData', formData);

    if (formData.reference_number && formData.season && formData.species_id) {
      checkSettlementExists(
        formData.reference_number,
        formData.season,
        formData.species_id,
        userProfile?.exporter_id || 0
      ).then((result) => {
        if (result.exists) {
          if (result.isBlocked) {
            setMainMessage(
              `❌ La referencia ${formData.reference_number} ya existe para esta temporada y especie y está bloqueada por lo que no puedes reemplazarla. Si aún así quieres hacerlo, se debe desbloquear y reintentar la subida.`
            );
          } else {
            setMainMessage(
              `⚠️ La referencia ${formData.reference_number} ya existe para esta temporada y especie. Al subirla, será reemplazada.`
            );
          }
        } else {
          setMainMessage(''); // Clear message if no settlement exists
        }
      });
    } else {
      setMainMessage(''); // Clear message if required fields are empty
    }
  }, [
    formData.reference_number,
    formData.season,
    formData.species_id,
    userProfile?.exporter_id,
  ]);

  const handleSearchShipment = async (): Promise<boolean> => {
    // Reset suggestion before attempting lookup
    setSuggestedContainerNumber('');
    if (
      !formData.reference_number ||
      !formData.season ||
      !formData.species_id ||
      !userProfile ||
      !session ||
      !selectedSpecies
    ) {
      toast.error('Completa referencia, temporada y especie antes de buscar.');
      return false;
    }

    const version = ++shipmentLookupVersion.current;
    const isCurrent = () => version === shipmentLookupVersion.current;
    setIsFetchingShipment(false);
    setIsFetchingContainer(false);
    setFetchShipmentError('');
    setShipmentAggregates(new Map());
    setShipmentLabels(new Map());
    setValidationShipmentBoxes([]);
    setIsFetchingShipment(true);
    setShipmentTripSuggestion(null);

    try {
      const shipmentBoxes =
        ((await getShipmentBoxesForReference(
          userProfile.exporter_id as number,
          formData.season,
          Number(formData.species_id),
          formData.reference_number,
          selectedDivisionId ? Number(selectedDivisionId) : null
        )) as ShipmentBoxWithQuadrature[]) || [];

      if (!isCurrent()) return false;
      if (shipmentBoxes.length === 0) {
        setFetchShipmentError(
          'No se encontraron despachos para la referencia ingresada'
        );
        return false;
      }

      // Suggest the container number found in the first shipment
      setSuggestedContainerNumber(shipmentBoxes[0]?.container_number || '');
      const firstShipment = shipmentBoxes[0];
      setFormData((prev) => ({
        ...prev,
        business_type:
          prev.business_type && prev.business_type.trim().length > 0
            ? prev.business_type
            : firstShipment.business_type || prev.business_type,
        sale_order:
          prev.sale_order && prev.sale_order.trim().length > 0
            ? prev.sale_order
            : firstShipment.sale_order || prev.sale_order,
        original_incoterm:
          firstShipment.original_incoterm || prev.original_incoterm,
      }));

      const { aggregates, labels, mappedBoxes } =
        buildShipmentAggregates(shipmentBoxes);

      setValidationShipmentBoxes(shipmentBoxes);
      setShipmentAggregates(aggregates);
      setShipmentLabels(labels);
      setShipmentTripSuggestion(buildTripSuggestionFromShipments(mappedBoxes));
    } catch (error) {
      if (!isCurrent()) return false;
      console.error('Error fetching shipment data:', error);
      setFetchShipmentError('Error al obtener los datos de despachos');
    } finally {
      if (isCurrent()) setIsFetchingShipment(false);
    }
    return true;
  };

  const handleSearchContainer = async (): Promise<boolean> => {
    // Reset suggestion before attempting lookup
    setSuggestedReferenceNumber('');
    if (
      !formData.container_number ||
      !formData.season ||
      !formData.species_id ||
      !userProfile ||
      !session ||
      !selectedSpecies
    ) {
      toast.error('Completa contenedor, temporada y especie antes de buscar.');
      return false;
    }

    const version = ++shipmentLookupVersion.current;
    const isCurrent = () => version === shipmentLookupVersion.current;
    setIsFetchingShipment(false);
    setIsFetchingContainer(false);
    setFetchShipmentError('');
    setShipmentAggregates(new Map());
    setShipmentLabels(new Map());
    setValidationShipmentBoxes([]);
    setIsFetchingContainer(true);
    setShipmentTripSuggestion(null);

    try {
      const shipmentBoxes =
        ((await getShipmentBoxesForContainer(
          userProfile.exporter_id as number,
          formData.season,
          Number(formData.species_id),
          formData.container_number
        )) as ShipmentBoxWithQuadrature[]) || [];

      if (!isCurrent()) return false;
      if (shipmentBoxes.length === 0) {
        setFetchShipmentError(
          'No se encontraron despachos para el contenedor ingresado'
        );
        return false;
      }

      // Suggest the reference number found in the first shipment
      setSuggestedReferenceNumber(shipmentBoxes[0]?.reference_number || '');

      const { aggregates, labels, mappedBoxes } =
        buildShipmentAggregates(shipmentBoxes);

      setValidationShipmentBoxes(shipmentBoxes);
      setShipmentAggregates(aggregates);
      setShipmentLabels(labels);
      setShipmentTripSuggestion(buildTripSuggestionFromShipments(mappedBoxes));
      // Optionally fill reference if it's empty; do NOT overwrite container input
      const first = shipmentBoxes[0];
      setFormData((prev) => ({
        ...prev,
        reference_number:
          prev.reference_number && prev.reference_number.trim().length > 0
            ? prev.reference_number
            : first.reference_number || prev.reference_number,
        // keep prev.container_number as typed by the user
        business_type:
          prev.business_type && prev.business_type.trim().length > 0
            ? prev.business_type
            : first.business_type || prev.business_type,
        sale_order:
          prev.sale_order && prev.sale_order.trim().length > 0
            ? prev.sale_order
            : first.sale_order || prev.sale_order,
        original_incoterm: first.original_incoterm || prev.original_incoterm,
      }));
    } catch (error) {
      if (!isCurrent()) return false;
      console.error('Error fetching shipment by container:', error);
      setFetchShipmentError('Error al obtener los datos de despachos');
    } finally {
      if (isCurrent()) setIsFetchingContainer(false);
    }
    return true;
  };

  // Create a stable key for settlement details to trigger recalculation
  // This ensures the cuadratura updates whenever any detail quantity changes
  const settlementDetailsKey = useMemo(() => {
    const details = formData.settlement_details || [];
    return details
      .map(
        (d) =>
          `${d.id}:${d.quantity}:${d.variety}:${d.caliber}:${d.color}:${d.format}:${d.label}:${d.unit_weight}:${d.category}`
      )
      .join('|');
  }, [formData.settlement_details]);

  // Compute comparison data synchronously using useMemo to ensure it updates
  // immediately when settlement details are edited
  const comparisonData = useMemo(() => {
    const settlementTotals = new Map<
      string,
      { product: string; quantity: number }
    >();
    formData.settlement_details.forEach((detail) => {
      const key = buildSettlementQuadratureProductKey(detail, {
        season: formData.season,
        speciesId: formData.species_id,
      });
      const current = settlementTotals.get(key);
      const quantity = (detail.quantity || 0) + (current?.quantity || 0);
      settlementTotals.set(key, {
        product: buildSettlementProductLabel(detail),
        quantity,
      });
    });

    const allKeys = new Set([
      ...shipmentAggregates.keys(),
      ...settlementTotals.keys(),
    ]);

    const areQuantitiesEqual = (a: number, b: number) =>
      Math.abs(a - b) < 0.0001;

    const hasValidComparisonFormat = (key: string) => {
      const format = key.split('|')[3];
      return isValidBoxFormat(format);
    };

    const comp = Array.from(allKeys).map((key) => {
      const shippedQuantity = shipmentAggregates.get(key) || 0;
      const settled = settlementTotals.get(key);
      const settledQuantity = settled?.quantity || 0;
      const product = settled?.product || shipmentLabels.get(key) || key;
      const hasValidFormat = hasValidComparisonFormat(key);

      return {
        key,
        product,
        shipped: shippedQuantity,
        settled: settledQuantity,
        isMatched:
          hasValidFormat &&
          areQuantitiesEqual(settledQuantity, shippedQuantity),
      };
    });

    comp.sort((a, b) => {
      if (a.isMatched !== b.isMatched) {
        return a.isMatched ? -1 : 1;
      }
      return a.product.localeCompare(b.product);
    });

    return comp;
  }, [
    shipmentAggregates,
    shipmentLabels,
    settlementDetailsKey,
    formData.settlement_details,
    formData.season,
    formData.species_id,
  ]);

  // Computed values derived from comparisonData
  const isPackingListValidationOK =
    missingFormatCodes.length === 0 &&
    comparisonData.length > 0 &&
    comparisonData.every((item) => item.isMatched);

  const comparisonTotals = useMemo(
    () =>
      comparisonData.reduce(
        (totals, row) => {
          totals.settled += row.settled;
          totals.shipped += row.shipped;
          return totals;
        },
        { settled: 0, shipped: 0 }
      ),
    [comparisonData]
  );

  const handleResolvedCostLinesChange = useCallback(
    (lines: ResolvedSettlementCostLine[]) => {
      setResolvedCostLines(lines);
    },
    []
  );
  const handleCostMappingsReadyChange = useCallback((ready: boolean) => {
    setCostMappingsReady(ready);
  }, []);

  useEffect(() => {
    const hadExtractedCostLines = costLines.length > 0;
    if (!hadExtractedCostLines && reviewCostLines.length === 0) return;
    if (resolvedCostLines.length !== reviewCostLines.length) return;

    const exchangeRate = Number(formData.exchange_rate) || 1;
    const localCurrency =
      normalizeCostCurrency(formData.local_currency) || 'USD';
    const totals = resolvedCostLines.reduce(
      (current, line) => {
        const currency = normalizeCostCurrency(line.currency);
        const localAmount =
          currency === 'USD' && localCurrency !== 'USD'
            ? line.amount * exchangeRate
            : line.amount;
        const usdAmount =
          currency === 'USD' ? line.amount : line.amount / exchangeRate;

        if (line.officialCategory === 'freight') {
          current.freight += usdAmount;
        } else {
          current[line.officialCategory] += localAmount;
        }
        return current;
      },
      {
        commission: 0,
        tax: 0,
        repacking: 0,
        other_costs: 0,
        freight: 0,
      }
    );

    setFormData((current) => {
      const nextValues = {
        total_commission_local: totals.commission,
        total_tax_local: totals.tax,
        total_repacking_costs_local: totals.repacking,
        total_other_costs_local: totals.other_costs,
        total_freight_usd: totals.freight,
      };
      const unchanged = Object.entries(nextValues).every(
        ([key, value]) =>
          Math.abs(
            Number(current[key as keyof SettlementFormData] || 0) - value
          ) < 0.000001
      );
      return unchanged ? current : { ...current, ...nextValues };
    });
  }, [
    costLines.length,
    reviewCostLines.length,
    formData.exchange_rate,
    formData.local_currency,
    resolvedCostLines,
  ]);

  const handleSubmit = async () => {
    const missingFields: string[] = [];

    if (!formData.market_id) {
      missingFields.push('mercado venta');
    }
    if (!requiresManualTrip() && !selectedShipId) {
      missingFields.push('nave');
    }
    if (!formData.reference_number || formData.reference_number == 'N/A') {
      missingFields.push('número de referencia');
    }
    if (!formData.exporter_receiver_id) {
      missingFields.push('cliente oficial');
    }
    if (!formData.container_number) {
      missingFields.push('número de contenedor');
    }
    if (!formData.date_sold) {
      missingFields.push('fecha de venta');
    }
    if (!formData.local_currency) {
      missingFields.push('moneda local');
    }
    if (!formData.original_incoterm) {
      missingFields.push('incoterm original');
    }

    if (missingFields.length > 0) {
      toast.error(
        `Por favor completa los siguientes campos: ${missingFields.join(', ')}`
      );
      return;
    }

    if (
      reviewCostLines.length > 0 &&
      (!costMappingsReady ||
        resolvedCostLines.length !== reviewCostLines.length)
    ) {
      toast.error('Espera a que terminen de cargar los mapeos de costos.');
      return;
    }

    if (resolvedCostLines.some((line) => line.amount < 0)) {
      toast.error(
        'V1 no admite costos negativos. Corrige la lectura antes de continuar.'
      );
      return;
    }

    const reviewedLocalCurrency = normalizeCostCurrency(
      formData.local_currency
    );
    const unsupportedCurrencyLine = resolvedCostLines.find((line) => {
      const currency = normalizeCostCurrency(line.currency);
      return currency !== 'USD' && currency !== reviewedLocalCurrency;
    });
    if (unsupportedCurrencyLine) {
      toast.error(
        `La moneda de ${unsupportedCurrencyLine.originalName} debe ser USD o ${reviewedLocalCurrency}.`
      );
      return;
    }

    const requiresExchangeRate = resolvedCostLines.some((line) => {
      const currency = normalizeCostCurrency(line.currency);
      return line.officialCategory === 'freight'
        ? currency !== 'USD'
        : currency === 'USD' && reviewedLocalCurrency !== 'USD';
    });
    if (requiresExchangeRate && Number(formData.exchange_rate) <= 0) {
      toast.error(
        'Ingresa un tipo de cambio válido para convertir los costos entre la moneda local y USD.'
      );
      return;
    }

    // Additional validation for manual trip creation (A/T ships)
    if (requiresManualTrip()) {
      if (
        !manualTripData.destino_market_id ||
        !manualTripData.fecha_embarque ||
        !manualTripData.fecha_llegada
      ) {
        toast.error(
          'Por favor completa todos los campos de embarque y destino'
        );
        return;
      }
    } else {
      // Validation for maritime ships (M)
      if (!formData.ship_trip_id) {
        toast.error('Por favor selecciona el viaje');
        return;
      }
    }

    if (
      formData.settlement_details.some(
        (detail) => ![...VALID_FORMATS].includes(detail.format || '')
      )
    ) {
      toast.error(
        'Hay algunos códigos de embalaje que no tienen un formato de caja asignado. Asigna los formatos NO ENCONTRADOS antes de subir la liquidación.'
      );
      return;
    }

    if (
      formData.settlement_details.some(
        (detail) => detail.variety == '' || detail.variety == 'N/A'
      )
    ) {
      toast.error(
        'Hay algunos detalles que no tienen una variedad asignada. Asigna las variedades antes de subir la liquidación.'
      );
      return;
    }

    if (allowedVarieties.length > 0 && unknownVarieties.length > 0) {
      toast.error(
        'Hay detalles con variedades fuera de las variedades oficiales de la especie. Corrígelas antes de subir la liquidación.'
      );
      return;
    }

    if (
      formData.settlement_details.some(
        (detail) => detail.label == '' || detail.label == 'N/A'
      )
    ) {
      toast.error(
        'Hay algunos detalles que no tienen una etiqueta asignada. Asigna las etiquetas antes de subir la liquidación.'
      );
      return;
    }

    if (
      formData.settlement_details.some(
        (detail) => detail.caliber == '' || detail.caliber == 'N/A'
      )
    ) {
      toast.error(
        'Hay algunos detalles que no tienen un calibre asignado. Asigna los calibres antes de subir la liquidación.'
      );
      return;
    }

    if (
      formData.settlement_details.some(
        (detail) => detail.unit_weight == 0 || detail.unit_weight == 'N/A'
      )
    ) {
      toast.error(
        'Hay algunos detalles que no tienen un peso asignado. Asigna los pesos antes de subir la liquidación.'
      );
      return;
    }

    if (
      formData.settlement_details.some(
        (detail) => !detail.quantity || detail.quantity == 0
      )
    ) {
      toast.error(
        'Hay algunos detalles que tienen cantidad 0. Asigna las cantidades antes de subir la liquidación.'
      );
      return;
    }

    // Add the discrepancy check before setLoading(true);
    if (
      !formData.reference_number ||
      !formData.season ||
      !formData.species_id ||
      !userProfile ||
      !session ||
      !selectedSpecies
    )
      return;

    const hasDiscrepancies =
      !fetchShipmentError && comparisonData.some((item) => !item.isMatched);
    if (hasDiscrepancies) {
      const proceed = await confirm({
        title: '¿Continuar con discrepancias?',
        message:
          'Existen discrepancias entre las cantidades despachadas y liquidadas.',
        confirmLabel: 'Continuar',
      });
      if (!proceed) return;
    }

    let sourceDocumentId: string | null = null;
    setLoading(true);
    try {
      // Calculate totals for distribution
      const totalRevenue = formData.settlement_details.reduce(
        (sum, detail) => sum + (detail.local_total_price || 0),
        0
      );
      const totalKilos = formData.settlement_details.reduce(
        (sum, detail) => sum + (detail.kilos || 0),
        0
      );

      // Calculate distribution rates
      const commissionRate =
        totalRevenue > 0 ? formData.total_commission_local / totalRevenue : 0;
      const taxRate =
        totalRevenue > 0 ? formData.total_tax_local / totalRevenue : 0;
      const otherCostsPerKilo =
        totalKilos > 0 ? formData.total_other_costs_local / totalKilos : 0;
      const repackingCostsPerKilo =
        totalKilos > 0 ? formData.total_repacking_costs_local / totalKilos : 0;
      const freightPerKilo =
        totalKilos > 0 ? formData.total_freight_usd / totalKilos : 0;

      // Distribute costs to each detail
      const detailsWithCosts = formData.settlement_details.map((detail) => {
        // Commission and tax: proportional by revenue
        const detailCommission =
          (detail.local_total_price || 0) * commissionRate;
        const detailTax = (detail.local_total_price || 0) * taxRate;

        // Other costs and freight: proportional by kilos
        const detailOtherCosts = (detail.kilos || 0) * otherCostsPerKilo;
        const detailRepackingCosts =
          (detail.kilos || 0) * repackingCostsPerKilo;
        const detailFreight = (detail.kilos || 0) * freightPerKilo;

        // Calculate FOB
        const detailFob =
          ((detail.local_total_price || 0) -
            detailTax -
            detailCommission -
            detailOtherCosts -
            detailRepackingCosts) /
            formData.exchange_rate -
          detailFreight;

        return {
          ...detail,
          local_total_tax: detailTax,
          local_total_commision: detailCommission,
          local_other_costs: detailOtherCosts,
          local_repacking_costs: detailRepackingCosts,
          usd_freight: detailFreight,
          usd_total_fob: detailFob,
          exchange_rate: formData.exchange_rate,
        };
      });

      // Correct floating-point remainder on the last detail
      applyRemainderCorrection(
        detailsWithCosts,
        'local_total_commision',
        formData.total_commission_local
      );
      applyRemainderCorrection(
        detailsWithCosts,
        'local_total_tax',
        formData.total_tax_local
      );
      applyRemainderCorrection(
        detailsWithCosts,
        'local_other_costs',
        formData.total_other_costs_local
      );
      applyRemainderCorrection(
        detailsWithCosts,
        'local_repacking_costs',
        formData.total_repacking_costs_local
      );
      applyRemainderCorrection(
        detailsWithCosts,
        'usd_freight',
        formData.total_freight_usd
      );
      if (detailsWithCosts.length > 0) {
        recalcFob(
          detailsWithCosts[detailsWithCosts.length - 1],
          formData.exchange_rate
        );
      }

      // Prepare settlement data for the new edge function
      const settlementData = {
        reference_number: formData.reference_number?.trim(),
        division_id: selectedDivisionId ? Number(selectedDivisionId) : null,
        exporter_receiver_id: formData.exporter_receiver_id,
        receiver: selectedExporterReceiver?.name || formData.receiver,
        legacy_receiver: formData.legacy_receiver || formData.receiver,
        business_type: formData.business_type || null,
        sale_order: formData.sale_order || null,
        container_number: formData.container_number?.trim(),
        date_sold: formData.date_sold,
        market_id: formData.market_id,
        comparable_market_id: formData.comparable_market_id,
        region: formData.region || '',
        total_revenue_local: totalRevenue,
        total_tax_local: formData.total_tax_local,
        total_commission_local: formData.total_commission_local,
        total_other_costs_local: formData.total_other_costs_local,
        total_repacking_costs_local: formData.total_repacking_costs_local,
        total_freight_usd: formData.total_freight_usd,
        exchange_rate: formData.exchange_rate,
        local_currency: formData.local_currency,
        original_incoterm: formData.original_incoterm,
        season: formData.season,
        species_id: formData.species_id,
        ...(requiresManualTrip()
          ? {
              ship_id: ships.find(
                (ship) =>
                  ship.type == transportType &&
                  ((ship.type == 'A' && ship.name == 'AEREO') ||
                    (ship.type == 'T' && ship.name == 'TERRESTRE'))
              )?.id,
              manual_trip_data: {
                fecha_embarque: manualTripData.fecha_embarque,
                destino_market_id: manualTripData.destino_market_id,
                fecha_llegada: manualTripData.fecha_llegada,
              },
            }
          : {
              ship_trip_id: formData.ship_trip_id,
            }),
      };

      if (inboxClaim) {
        const prepared = await inboxRequest<{ sourceDocumentId: string }>(
          'prepare',
          {
            id: inboxClaim.id,
            token: inboxClaim.token,
            receiverId: formData.exporter_receiver_id,
          }
        );
        sourceDocumentId = prepared.sourceDocumentId;
      } else if (sourceFile) {
        const sourceDocument = await uploadOriginalSettlementDocument({
          file: sourceFile,
          exporterId: userProfile.exporter_id,
          exporterReceiverId: formData.exporter_receiver_id,
          userId: userProfile.id,
          sourceFlow: 'fruitmarkets',
        });
        sourceDocumentId = sourceDocument.id;
      }

      // Call the new edge function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-settlement-advanced`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            settlement_data: settlementData,
            settlement_details: detailsWithCosts,
            source_document_id: sourceDocumentId,
            inbox_claim_token: inboxClaim?.token,
            cost_lines: resolvedCostLines.map((line) => ({
              id: line.id,
              originalName: line.originalName,
              normalizedName: line.normalizedName,
              amount: line.amount,
              currency: line.currency,
              suggestedOfficialCategory: line.suggestedOfficialCategory,
              officialCategory: line.officialCategory,
              confidence: line.confidence ?? null,
              sourceLocation: line.sourceLocation || {},
              traceabilityLevel: line.traceabilityLevel,
            })),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear la liquidación');
      }

      const result = await response.json();

      const toastId = toast.loading(
        result.replaced_existing
          ? 'Liquidación actualizada exitosamente. Calculando comparación de precios de venta...'
          : 'Liquidación creada exitosamente. Calculando comparación de precios de venta...'
      );

      try {
        // Call the new edge function
        const response2 = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calculate-settlement-market-comparison`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({
              settlement_id: result.settlement_id,
              // On upload, compute both date_sold and ETA comparisons
              modifyMarketDateSold: true,
              modifyMarketEta: true,
            }),
          }
        );

        if (!response2.ok) {
          const errorData = await response2.json();
          throw new Error(
            errorData.error ||
              'Error al calcular la comparación de precios de venta'
          );
        }

        await response2.json();
        toast.update(toastId, {
          render: 'Comparación de precios de venta calculada exitosamente.',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      } catch (error) {
        toast.update(toastId, {
          render: 'Error al calcular la comparación de precios de venta',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
      }
      resetForm();
      onSuccess();
      onClose();
    } catch (error) {
      if (sourceDocumentId && !inboxClaim) {
        await markSettlementDocumentFailed(sourceDocumentId);
      }
      console.error('Error creating settlement:', error);
      toast.error(
        error instanceof Error ? error.message : 'Error al crear la liquidación'
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = useCallback(() => {
    setFormData(buildInitialFormData());
    setResolvedCostLines([]);
    setReviewCostLines(costLines);
    setCostMappingsReady(costLines.length === 0);
    setIsSourcePreviewOpen(false);
    setLoading(false);
    setSelectedShipTripId('');
    setEditingCell(null);
    setEditingCostField(null);
    setIsAddingNewDetail(false);
    setNewDetailData({});
    setSuggestedMarketName('');
    setTransportType('');
    setSelectedShipId('');
    setSuggestedShipName('');
    setMainMessage('');
    setCheckFormats(false);
    setCopyMode(null);
    setSelectedRowsForCopy(new Set());
    setManualTripData({
      embarque_market_id: '',
      fecha_embarque: '',
      destino_market_id: '',
      fecha_llegada: '',
    });
    setSuggestedContainerNumber('');
    setSuggestedReferenceNumber('');
    setShipmentAggregates(new Map());
    setShipmentLabels(new Map());
    setValidationShipmentBoxes([]);
    setInlineFormatSelections({});
    setLocalFormatOverrides({});
    setSavingInlineFormatCode(null);
    setFetchShipmentError('');
    setIsFetchingShipment(false);
    setIsFetchingContainer(false);
    setUnmappedFormatCount(0);
    setMarketComments([]);
    setIsLoadingMarketComments(false);
    setMarketCommentsError('');
    setShowMarketComments(false);
    setMarketCommentImageUrls({});
    setUnknownLabels([]);
    setUnknownVarieties([]);
    setShipmentTripSuggestion(null);
    setUngroupedDetails([]);
    setIsGrouped(false);
    setHighlightedRowId(null);
    setFreightLocalSnapshot(null);
    setHasAutoSearched(false);
    marketCommentsCountRef.current = 0;
  }, [buildInitialFormData, costLines]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleCellEdit = (detailId: number, field: string, value: string) => {
    setEditingCell({ detailId, field, value });
  };

  const handleCellSave = async () => {
    if (!editingCell) return;

    const { detailId, field, value } = editingCell;

    // Get the box_type of the detail being edited (for format updates)
    const editedDetail = formData.settlement_details.find(
      (d) => d.id === detailId
    );
    const boxTypeToUpdate = editedDetail?.box_type;

    // Update the detail in formData
    setFormData((prev) => ({
      ...prev,
      settlement_details: prev.settlement_details.map((detail) => {
        if (detail.id === detailId) {
          const updatedDetail = { ...detail } as Partial<
            SettlementDetails & { category: string }
          >;

          // Handle different field types
          if (field === 'unit_weight') {
            const numValue = parseFloat(value) || 0;
            updatedDetail.unit_weight = numValue;

            // Recalculate kilos
            const quantity = detail.quantity || 0;
            updatedDetail.kilos = numValue * quantity;
          } else if (field === 'quantity') {
            const numValue = parseFloat(value) || 0;
            updatedDetail.quantity = numValue;

            // Recalculate kilos
            const unitWeight = detail.unit_weight || 0;
            updatedDetail.kilos = unitWeight * numValue;

            // Update total price if we know price per bulto
            if (detail.local_total_price && detail.quantity) {
              const pricePerBulto = detail.local_total_price / detail.quantity;
              updatedDetail.local_total_price = pricePerBulto * numValue;
            }
          } else if (field === 'price_per_bulto') {
            // Calculate total price from price per bulto
            const pricePerBulto = parseFloat(value) || 0;
            const quantity = detail.quantity || 1;
            updatedDetail.local_total_price = pricePerBulto * quantity;
          } else if (field === 'local_total_price') {
            updatedDetail.local_total_price = parseFloat(value) || 0;
          } else if (field === 'pallet') {
            updatedDetail.pallet = value;
          } else if (field === 'variety') {
            updatedDetail.variety = value;
          } else if (field === 'format') {
            if (isManualUpload && [...VALID_FORMATS].includes(value)) {
              saveMapping(
                detail.box_type || '',
                value as (typeof VALID_FORMATS)[number],
                userProfile.exporter_id
              );
            }
            updatedDetail.format = value;
          } else if (field === 'caliber') {
            updatedDetail.caliber = value;
          } else if (field === 'color') {
            updatedDetail.color = value;
          } else if (field === 'label') {
            updatedDetail.label = value;
          } else if (field === 'category') {
            updatedDetail.category = value;
          }

          return updatedDetail;
        } else if (
          field === 'format' &&
          boxTypeToUpdate &&
          detail.box_type === boxTypeToUpdate
        ) {
          // Update format for all other details with the same box_type
          return {
            ...detail,
            format: value,
          };
        }
        return detail;
      }),
    }));

    // Recalculate total revenue if price changed
    if (
      editingCell.field === 'local_total_price' ||
      editingCell.field === 'price_per_bulto' ||
      editingCell.field === 'quantity'
    ) {
      setFormData((prev) => ({
        ...prev,
        total_revenue_local: prev.settlement_details.reduce(
          (sum, detail) => sum + (detail.local_total_price || 0),
          0
        ),
      }));
    }

    // Notify user if multiple details were updated (for format field)
    if (editingCell.field === 'format' && boxTypeToUpdate) {
      const affectedCount = formData.settlement_details.filter(
        (d) => d.box_type === boxTypeToUpdate
      ).length;
      if (affectedCount > 1) {
        toast.success(
          `Formato actualizado en ${affectedCount} detalles con el mismo Código Embalaje (${boxTypeToUpdate})`
        );
      }
    }

    setEditingCell(null);
  };

  const handleCellCancel = () => {
    setEditingCell(null);
  };

  const handleCostFieldEdit = (
    field:
      | 'commission'
      | 'tax'
      | 'freight'
      | 'otros'
      | 'repacking'
      | 'exchange_rate',
    value: string
  ) => {
    setEditingCostField({ field, value });
  };

  const handleCostFieldSave = async () => {
    if (!editingCostField) return;

    const { field, value } = editingCostField;
    const numValue = parseFloat(value) || 0;

    if (numValue < 0) {
      toast.error('Los costos deben ser positivos o cero.');
      return;
    }

    const fieldMap = {
      commission: 'total_commission_local',
      tax: 'total_tax_local',
      freight: 'total_freight_usd',
      otros: 'total_other_costs_local',
      repacking: 'total_repacking_costs_local',
      exchange_rate: 'exchange_rate',
    };

    if (field !== 'exchange_rate' && reviewCostLines.length > 0) {
      const categoryByField: Record<
        'commission' | 'tax' | 'freight' | 'otros' | 'repacking',
        OfficialCostCategory
      > = {
        commission: 'commission',
        tax: 'tax',
        freight: 'freight',
        otros: 'other_costs',
        repacking: 'repacking',
      };
      setEditingCostField(null);
      setCostEditorCategory(categoryByField[field]);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [fieldMap[field]]: numValue,
    }));

    // Update local snapshot if freight (USD) is edited and local currency is not USD
    if (
      field === 'freight' &&
      formData.local_currency?.toUpperCase() !== 'USD'
    ) {
      setFreightLocalSnapshot(numValue * (formData.exchange_rate || 1));
    }

    setEditingCostField(null);
  };

  const handleCostFieldCancel = () => {
    setEditingCostField(null);
  };

  // Copy functionality handlers
  const handleCellCopy = (
    detailId: number,
    field: 'caliber' | 'color' | 'label' | 'variety' | 'format' | 'category',
    value: string
  ) => {
    setCopyMode({ field, value });
    setSelectedRowsForCopy(new Set());
    setEditingCell(null); // Exit edit mode
  };

  const handleRowSelectionForCopy = (detailId: number, isSelected: boolean) => {
    setSelectedRowsForCopy((prev) => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(detailId);
      } else {
        newSet.delete(detailId);
      }
      return newSet;
    });
  };

  const handleConfirmCopyToRows = () => {
    if (!copyMode) return;

    const { field, value } = copyMode;

    setFormData((prev) => ({
      ...prev,
      settlement_details: prev.settlement_details.map((detail) => {
        if (selectedRowsForCopy.has(detail.id!)) {
          return {
            ...detail,
            [field]: value,
          };
        }
        return detail;
      }),
    }));

    // Exit copy mode
    setCopyMode(null);
    setSelectedRowsForCopy(new Set());
    toast.success(`Valor copiado a ${selectedRowsForCopy.size} filas`);
  };

  const handleCancelCopyMode = () => {
    setCopyMode(null);
    setSelectedRowsForCopy(new Set());
  };

  const handleAddNewDetail = () => {
    setIsAddingNewDetail(true);
    if (formData.settlement_details.length > 0) {
      const firstDetail = formData.settlement_details[0];
      setNewDetailData({
        ...firstDetail,
        id: -(formData.settlement_details.length + 1),
      });
    } else {
      setNewDetailData({
        format: 'CAJA',
        unit_weight: 5,
        quantity: 1,
        local_total_price: 0,
        category: '',
      });
    }
  };

  const handleConfirmNewDetail = async () => {
    const unitWeight = Number(newDetailData.unit_weight) || 0;
    const quantity = Number(newDetailData.quantity) || 0;
    const newKilos = unitWeight * quantity;

    const newDetailComplete = {
      ...newDetailData,
      id: -(formData.settlement_details.length + 1),
      kilos: newKilos,
      local_currency: formData.local_currency,
      exchange_rate: formData.exchange_rate,
      incoterm: formData.original_incoterm,
      date_sold: formData.date_sold,
      local_total_tax: 0,
      local_total_commision: 0,
      local_other_costs: 0,
      local_repacking_costs: 0,
      usd_freight: 0,
      usd_total_fob: 0,
      quartil_vs_market: null,
      difference_vs_market: null,
    };

    setFormData((prev) => ({
      ...prev,
      settlement_details: [...prev.settlement_details, newDetailComplete],
      total_revenue_local:
        prev.total_revenue_local +
        (Number(newDetailData.local_total_price) || 0),
    }));

    setIsAddingNewDetail(false);
    setNewDetailData({});
  };

  const handleCancelNewDetail = () => {
    setIsAddingNewDetail(false);
    setNewDetailData({});
  };

  const handleNewDetailFieldChange = (field: string, value: string) => {
    const numericFields = ['unit_weight', 'quantity', 'local_total_price'];
    const processedValue = numericFields.includes(field)
      ? parseFloat(value) || 0
      : value;

    setNewDetailData((prev) => {
      const updated = {
        ...prev,
        [field]: processedValue,
      };

      // Update kilos when unit_weight or quantity changes
      if (field === 'unit_weight' || field === 'quantity') {
        const unitWeight =
          field === 'unit_weight'
            ? processedValue
            : Number(prev.unit_weight) || 0;
        const quantity =
          field === 'quantity' ? processedValue : Number(prev.quantity) || 0;
        updated.kilos = Number(unitWeight) * Number(quantity);
      }

      return updated;
    });
  };

  const handleDeleteDetail = async (detailId: number) => {
    if (formData.settlement_details.length <= 1) return;

    const detailToDelete = formData.settlement_details.find(
      (d) => d.id === detailId
    );

    setFormData((prev) => ({
      ...prev,
      settlement_details: prev.settlement_details.filter(
        (detail) => detail.id !== detailId
      ),
      total_revenue_local:
        prev.total_revenue_local - (detailToDelete?.local_total_price || 0),
    }));
    toast.success('Detalle eliminado');
  };

  const handleDuplicateDetail = (detailId: number) => {
    const detailIndex = formData.settlement_details.findIndex(
      (d) => d.id === detailId
    );
    if (detailIndex === -1) return;

    const detailToDuplicate = formData.settlement_details[detailIndex];

    const duplicatedDetail = {
      ...detailToDuplicate,
      id: -(
        Math.abs(
          Math.min(...formData.settlement_details.map((d) => d.id || 0))
        ) + 1
      ),
    };

    // Insert the duplicated detail right after the original
    const newDetails = [...formData.settlement_details];
    newDetails.splice(detailIndex + 1, 0, duplicatedDetail);

    setFormData((prev) => ({
      ...prev,
      settlement_details: newDetails,
      total_revenue_local:
        prev.total_revenue_local + (duplicatedDetail.local_total_price || 0),
    }));

    // Highlight the duplicated row
    setHighlightedRowId(duplicatedDetail.id!);

    // Remove highlight after 2 seconds
    setTimeout(() => {
      setHighlightedRowId(null);
    }, 2000);

    toast.success('Detalle duplicado');
  };

  const handleGroupByProduct = () => {
    if (isGrouped) {
      // Ungroup - restore original details
      setFormData((prev) => ({
        ...prev,
        settlement_details: ungroupedDetails,
        total_revenue_local: ungroupedDetails.reduce(
          (sum, detail) => sum + (detail.local_total_price || 0),
          0
        ),
      }));
      setIsGrouped(false);
      toast.success(
        `Detalles desagrupados: ${formData.settlement_details.length} líneas → ${ungroupedDetails.length} líneas`
      );
    } else {
      // Group - save current details first
      setUngroupedDetails([...formData.settlement_details]);

      // Group details by: Variedad, Formato, Calibre, Color, Etiqueta, Categoria, Peso Bulto
      const grouped = new Map<
        string,
        Partial<SettlementDetails & { box_type: string }>
      >();

      formData.settlement_details.forEach((detail) => {
        const key = `${detail.variety}-${detail.format}-${detail.caliber}-${detail.color}-${detail.label}-${detail.category}-${detail.unit_weight}`;

        if (grouped.has(key)) {
          const existing = grouped.get(key)!;
          existing.quantity = (existing.quantity || 0) + (detail.quantity || 0);
          existing.kilos = (existing.kilos || 0) + (detail.kilos || 0);
          existing.local_total_price =
            (existing.local_total_price || 0) + (detail.local_total_price || 0);
        } else {
          grouped.set(key, {
            ...detail,
            pallet: '', // Leave pallet empty as we're grouping
          });
        }
      });

      // Convert map to array with new IDs
      const groupedDetails = Array.from(grouped.values()).map(
        (detail, index) => ({
          ...detail,
          id: -(index + 1),
        })
      );

      setFormData((prev) => ({
        ...prev,
        settlement_details: groupedDetails,
      }));

      setIsGrouped(true);

      toast.success(
        `Detalles agrupados: ${formData.settlement_details.length} líneas → ${groupedDetails.length} líneas`
      );
    }
  };

  const renderEditableCell = (
    detailId: number,
    field: string,
    value: string
  ) => {
    const isEditing =
      editingCell?.detailId === detailId && editingCell?.field === field;
    const canCopy = [
      'caliber',
      'color',
      'label',
      'variety',
      'format',
      'category',
    ].includes(field);

    if (copyMode !== null) {
      return (
        <div className='flex items-center'>
          <p>{value}</p>
        </div>
      );
    }
    if (isEditing) {
      if (field === 'format') {
        return (
          <div className='flex items-center gap-1'>
            <Select
              size='sm'
              selectedKeys={[editingCell.value]}
              onChange={(e) =>
                setEditingCell((prev) =>
                  prev ? { ...prev, value: e.target.value } : null
                )
              }
              className='min-w-[120px]'
            >
              {VALID_FORMATS.map((format) => (
                <SelectItem key={format} value={format}>
                  {format}
                </SelectItem>
              ))}
            </Select>
            <Button
              isIconOnly
              size='sm'
              color='success'
              onClick={handleCellSave}
            >
              <Icon icon='tabler:check' />
            </Button>
            <Button
              isIconOnly
              size='sm'
              color='danger'
              onClick={handleCellCancel}
            >
              <Icon icon='tabler:x' />
            </Button>
            {canCopy && (
              <Button
                // isIconOnly
                size='sm'
                color='primary'
                onClick={() =>
                  handleCellCopy(
                    detailId,
                    field as
                      | 'caliber'
                      | 'color'
                      | 'label'
                      | 'variety'
                      | 'format'
                      | 'category',
                    editingCell.value
                  )
                }
              >
                <Icon icon='tabler:copy' /> Copiar
              </Button>
            )}
          </div>
        );
      } else if (field === 'color' && formData.species_id == 2) {
        return (
          <div className='flex items-center'>
            <Select
              size='sm'
              selectedKeys={[editingCell.value]}
              onChange={(e) =>
                setEditingCell({ ...editingCell, value: e.target.value })
              }
              className='min-w-[80px]'
            >
              <SelectItem key='L' value='L'>
                L
              </SelectItem>
              <SelectItem key='D' value='D'>
                D
              </SelectItem>
              <SelectItem key='DD' value='DD'>
                DD
              </SelectItem>
            </Select>
            <div className='flex ml-2'>
              <Button
                isIconOnly
                size='sm'
                color='success'
                onClick={handleCellSave}
              >
                <Icon icon='tabler:check' />
              </Button>
              <Button
                isIconOnly
                size='sm'
                color='danger'
                onClick={handleCellCancel}
                className='ml-1'
              >
                <Icon icon='tabler:x' />
              </Button>
              {canCopy && (
                <Button
                  // isIconOnly
                  size='sm'
                  color='primary'
                  onClick={() =>
                    handleCellCopy(
                      detailId,
                      field as
                        | 'caliber'
                        | 'color'
                        | 'label'
                        | 'variety'
                        | 'format'
                        | 'category',
                      editingCell.value
                    )
                  }
                  className='ml-1'
                >
                  <Icon icon='tabler:copy' /> Copiar
                </Button>
              )}
            </div>
          </div>
        );
      }
      return (
        <div className='flex items-center gap-1'>
          <Input
            size='sm'
            value={editingCell.value}
            onChange={(e) =>
              setEditingCell((prev) =>
                prev ? { ...prev, value: e.target.value.toUpperCase() } : null
              )
            }
            className='min-w-[80px]'
            autoFocus
            onFocus={(e) => (e.target as HTMLInputElement).select()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCellSave();
              if (e.key === 'Escape') handleCellCancel();
            }}
          />
          <Button isIconOnly size='sm' color='success' onClick={handleCellSave}>
            <Icon icon='tabler:check' />
          </Button>
          <Button
            isIconOnly
            size='sm'
            color='danger'
            onClick={handleCellCancel}
          >
            <Icon icon='tabler:x' />
          </Button>
          {canCopy && (
            <Button
              // isIconOnly
              size='sm'
              color='primary'
              onClick={() =>
                handleCellCopy(
                  detailId,
                  field as
                    | 'caliber'
                    | 'color'
                    | 'label'
                    | 'variety'
                    | 'format'
                    | 'category',
                  editingCell.value
                )
              }
            >
              <Icon icon='tabler:copy' /> Copiar
            </Button>
          )}
        </div>
      );
    }

    return (
      <div
        className='cursor-pointer hover:bg-default-200 p-1 rounded min-h-[32px] flex items-center'
        onClick={() => handleCellEdit(detailId, field, value)}
      >
        {value || '-'}
      </div>
    );
  };

  const renderEditableCostField = (
    field:
      | 'commission'
      | 'tax'
      | 'freight'
      | 'otros'
      | 'repacking'
      | 'exchange_rate',
    value: number | undefined,
    currency: string
  ) => {
    const categoryByField: Partial<Record<typeof field, OfficialCostCategory>> =
      {
        commission: 'commission',
        tax: 'tax',
        freight: 'freight',
        otros: 'other_costs',
        repacking: 'repacking',
      };
    const sourceCategory = categoryByField[field];
    const opensSourceEditor =
      reviewCostLines.length > 0 && sourceCategory !== undefined;
    const isEditing = editingCostField?.field === field;

    if (isEditing) {
      return (
        <div className='flex items-center gap-1'>
          <Input
            size='sm'
            value={editingCostField.value}
            onChange={(e) =>
              setEditingCostField((prev) =>
                prev ? { ...prev, value: e.target.value } : null
              )
            }
            className='w-24'
            autoFocus
            onFocus={(e) => (e.target as HTMLInputElement).select()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCostFieldSave();
              if (e.key === 'Escape') handleCostFieldCancel();
            }}
          />
          <span className='text-xs'>{currency}</span>
          <Button
            isIconOnly
            size='sm'
            color='success'
            onClick={handleCostFieldSave}
          >
            <Icon icon='tabler:check' />
          </Button>
          <Button
            isIconOnly
            size='sm'
            color='danger'
            onClick={handleCostFieldCancel}
          >
            <Icon icon='tabler:x' />
          </Button>
        </div>
      );
    }

    return (
      <div
        className='cursor-pointer bg-default-200 hover:bg-default-300 p-2 rounded flex items-center gap-1'
        onClick={() => {
          if (opensSourceEditor) {
            setCostEditorCategory(sourceCategory);
            return;
          }
          handleCostFieldEdit(field, value?.toString() || '0');
        }}
        title={
          opensSourceEditor
            ? 'Ver y editar las líneas originales de esta categoría'
            : undefined
        }
      >
        <span className='font-medium'>{value?.toLocaleString()}</span>
        <span className='text-xs'>{currency}</span>
      </div>
    );
  };

  if (!isOpen || !excelData) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        resetForm();
      }}
      //   size="full"
      className='w-[95vw] max-w-[95vw] h-[90vh] mx-auto my-auto'
      scrollBehavior='inside'
      isDismissable={!loading}
      hideCloseButton={loading}
    >
      <ModalContent className='relative'>
        {addClientScope && (
          <AddClientModal
            key={`${addClientScope.exporterId}:${addClientScope.version}`}
            isOpen
            onClose={handleCloseAddClient}
            exporterId={addClientScope.exporterId}
            existingClientNames={exporterReceivers.map(
              (receiver) => receiver.name
            )}
            onClientAdded={handleClientAdded}
          />
        )}
        {isSearchingShipments && (
          <div className='absolute inset-0 z-50 flex flex-col items-center justify-center bg-background/70 backdrop-blur-sm'>
            <Spinner
              label='Buscando información en Base de Despachos...'
              size='lg'
            />
          </div>
        )}
        <div
          className={`absolute inset-0 z-[60] items-center justify-center bg-black/40 p-4 backdrop-blur-[1px] ${
            costEditorCategory === null ? 'hidden' : 'flex'
          }`}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setCostEditorCategory(null);
            }
          }}
          onKeyDownCapture={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              setCostEditorCategory(null);
            }
          }}
        >
          <div
            role='dialog'
            aria-modal='true'
            aria-labelledby='original-cost-editor-title'
            className='flex max-h-[78vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-divider bg-background shadow-2xl'
          >
            <div className='flex flex-wrap items-start justify-between gap-3 border-b border-divider px-5 py-4'>
              <div>
                <h3
                  id='original-cost-editor-title'
                  className='text-xl font-bold'
                >
                  Editar costos originales
                </h3>
                <p className='text-sm text-default-500'>
                  Edita montos y monedas, mueve líneas entre categorías o agrega
                  costos manuales.
                </p>
              </div>
              <Button
                isIconOnly
                size='sm'
                variant='light'
                aria-label='Cerrar editor de costos originales'
                onPress={() => setCostEditorCategory(null)}
              >
                <Icon icon='tabler:x' />
              </Button>
            </div>

            <div className='flex-1 overflow-y-auto p-5'>
              <div className='space-y-4'>
                <Select
                  size='sm'
                  label='Mostrar categoría'
                  className='max-w-xs'
                  selectedKeys={[costEditorCategory || 'all']}
                  onSelectionChange={(keys) => {
                    const category = Array.from(keys)[0];
                    if (typeof category !== 'string') return;
                    setCostEditorCategory(
                      category as OfficialCostCategory | 'all'
                    );
                  }}
                >
                  <SelectItem key='all' value='all'>
                    Todas las categorías
                  </SelectItem>
                  {OFFICIAL_COST_CATEGORIES.map((category) => (
                    <SelectItem key={category} value={category}>
                      {OFFICIAL_COST_CATEGORY_LABELS[category]}
                    </SelectItem>
                  ))}
                </Select>

                {exporterCostEditorFilter !== undefined && (
                  <Chip
                    variant='flat'
                    onClose={() => setExporterCostEditorFilter(undefined)}
                  >
                    Categoría exportadora:{' '}
                    {exporterCostEditorFilter === null
                      ? 'Sin clasificar'
                      : receiverExporterCosts.categories.find(
                          (category) => category.id === exporterCostEditorFilter
                        )?.name}
                  </Chip>
                )}
                <ReceiverCostClassificationTable
                  costLines={reviewCostLines}
                  localCurrency={formData.local_currency}
                  onCostLinesChange={setReviewCostLines}
                  exporterId={userProfile?.exporter_id}
                  exporterReceiverId={formData.exporter_receiver_id}
                  onResolvedLinesChange={handleResolvedCostLinesChange}
                  onMappingsReadyChange={handleCostMappingsReadyChange}
                  categoryFilter={costEditorCategory || 'all'}
                  exporterCategoryFilter={exporterCostEditorFilter}
                />
              </div>
            </div>

            <div className='flex justify-end border-t border-divider px-5 py-3'>
              <Button
                color='primary'
                onPress={() => setCostEditorCategory(null)}
              >
                Listo
              </Button>
            </div>
          </div>
        </div>
        <ModalHeader className='flex flex-row flex-wrap items-start justify-between gap-3'>
          <div className='flex flex-col gap-1'>
            <h3 className='text-xl font-bold'>
              Previsualización de Liquidación
            </h3>
            <p className='text-sm text-default-500'>
              Revisa y edita los datos antes de crear la liquidación
            </p>
          </div>
          {sourceFile && !isSourcePreviewOpen && (
            <Button
              size='sm'
              variant='flat'
              onPress={() => setIsSourcePreviewOpen(true)}
            >
              Ver archivo original
            </Button>
          )}
        </ModalHeader>

        <ModalBody className='min-h-0 overflow-hidden'>
          <div
            className={`grid h-full min-h-0 gap-4 ${
              isSourcePreviewOpen && sourceFile
                ? 'grid-rows-[minmax(0,1fr)_minmax(260px,45%)] lg:grid-cols-[minmax(0,1fr)_minmax(420px,42%)] lg:grid-rows-1'
                : 'grid-cols-1'
            }`}
          >
            <div className='min-h-0 min-w-0 space-y-8 overflow-y-auto pr-1'>
              <div>
                {mainMessage && (
                  <Alert variant='warning' className='mb-4'>
                    {mainMessage}
                  </Alert>
                )}
                <h2 className='text-xl font-semibold mb-4'>General</h2>
                <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                  <Select
                    label='Especie'
                    selectedKeys={[formData.species_id.toString()]}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        species_id: parseInt(e.target.value),
                      })
                    }
                    //   description="Especie de fruta seleccionada en la carga de Excel"
                  >
                    {species.map((species) => (
                      <SelectItem
                        key={species.id}
                        value={species.id.toString()}
                      >
                        {species.name}
                      </SelectItem>
                    ))}
                  </Select>
                  <Select
                    label='Temporada'
                    selectedKeys={[formData.season]}
                    onChange={(e) =>
                      setFormData({ ...formData, season: e.target.value })
                    }
                    description={getSeasonMismatchMessage(
                      formData.date_sold,
                      formData.season
                    )}
                    color={
                      !isSeasonDateAligned(formData.date_sold, formData.season)
                        ? 'warning'
                        : 'default'
                    }
                  >
                    {Object.values(SEASONS).map((season) => (
                      <SelectItem key={season.name} value={season.name}>
                        {season.name}
                      </SelectItem>
                    ))}
                  </Select>

                  <Input
                    label='Número de Referencia'
                    value={formData.reference_number}
                    description={
                      suggestedReferenceNumber
                        ? `Sugerido desde despachos: ${suggestedReferenceNumber}`
                        : undefined
                    }
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        reference_number: e.target.value,
                      })
                    }
                    onBlur={() =>
                      setFormData({
                        ...formData,
                        reference_number: formData.reference_number?.trim(),
                      })
                    }
                  />
                  {divisions.length > 1 && (
                    <Select
                      label='División'
                      selectedKeys={[selectedDivisionId || 'auto']}
                      onChange={(e) =>
                        setSelectedDivisionId(
                          e.target.value === 'auto' ? '' : e.target.value
                        )
                      }
                    >
                      {[
                        <SelectItem key='auto' value='auto'>
                          Automática (según despacho)
                        </SelectItem>,
                        ...divisions.map((division) => (
                          <SelectItem key={division.id} value={division.id}>
                            {division.name}
                          </SelectItem>
                        )),
                      ]}
                    </Select>
                  )}
                  <Input
                    label='Número de Contenedor'
                    value={formData.container_number}
                    description={
                      suggestedContainerNumber
                        ? `Sugerido desde despachos: ${suggestedContainerNumber}`
                        : undefined
                    }
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        container_number: e.target.value,
                      })
                    }
                    onBlur={() =>
                      setFormData({
                        ...formData,
                        container_number: formData.container_number?.trim(),
                      })
                    }
                  />
                  <Input
                    label='Fecha de Venta'
                    type='date'
                    value={formData.date_sold?.split('T')[0]}
                    onChange={(e) => {
                      const newDate = e.target.value;
                      setFormData({
                        ...formData,
                        date_sold: newDate,
                      });
                    }}
                    description={getSeasonMismatchMessage(
                      formData.date_sold,
                      formData.season
                    )}
                    color={
                      !isSeasonDateAligned(formData.date_sold, formData.season)
                        ? 'warning'
                        : 'default'
                    }
                  />
                  <Autocomplete
                    label='Cliente'
                    defaultFilter={filterClientOption}
                    selectedKey={
                      formData.exporter_receiver_id
                        ? formData.exporter_receiver_id.toString()
                        : null
                    }
                    onSelectionChange={(key) => {
                      if (key?.toString() === ADD_CLIENT_OPTION_KEY) {
                        handleOpenAddClient();
                        return;
                      }
                      const receiver = exporterReceivers.find(
                        (item) => item.id.toString() === key?.toString()
                      );
                      setFormData((current) => ({
                        ...current,
                        exporter_receiver_id: receiver?.id || null,
                        receiver: receiver?.name || '',
                        legacy_receiver:
                          current.legacy_receiver || current.receiver,
                      }));
                    }}
                    isRequired
                    description={
                      formData.legacy_receiver &&
                      normalizeReceiverName(formData.legacy_receiver) !==
                        normalizeReceiverName(formData.receiver)
                        ? `${receiverNameSource === 'shipment' ? 'Nombre en despacho' : 'Nombre en liquidación'}: ${formData.legacy_receiver}`
                        : undefined
                    }
                  >
                    {[
                      <AutocompleteItem
                        key={ADD_CLIENT_OPTION_KEY}
                        textValue={ADD_CLIENT_OPTION_LABEL}
                        startContent={<Icon icon='tabler:plus' />}
                        isDisabled={!userProfile?.exporter_id}
                      >
                        Añadir nuevo cliente
                      </AutocompleteItem>,
                      ...exporterReceivers.map((receiver) => (
                        <AutocompleteItem
                          key={receiver.id.toString()}
                          textValue={receiver.name}
                        >
                          {receiver.name}
                        </AutocompleteItem>
                      )),
                    ]}
                  </Autocomplete>

                  <Autocomplete
                    label='Mercado Venta'
                    selectedKey={formData.market_id}
                    onSelectionChange={(key) => {
                      const newMarketId = key?.toString() || '';
                      setFormData({ ...formData, market_id: newMarketId });

                      // Auto-sync Puerto Destino when in A/T mode
                      if (requiresManualTrip() && newMarketId) {
                        setManualTripData((prev) => ({
                          ...prev,
                          destino_market_id: newMarketId,
                        }));
                      }
                    }}
                    description={
                      suggestedMarketName
                        ? `💡 No se encontró coincidencia exacta. En el Excel se encontró "${suggestedMarketName}". Por favor selecciona el mercado correcto arriba.`
                        : ''
                    }
                    placeholder='Busca un mercado...'
                    defaultItems={markets}
                  >
                    {(market) => (
                      <AutocompleteItem
                        key={market.id}
                        textValue={`${market.name}, ${market.country}`}
                      >
                        {`${market.name}, ${market.country}`}
                      </AutocompleteItem>
                    )}
                  </Autocomplete>
                  <Select
                    label='Mercado Comparable'
                    selectedKeys={[formData.comparable_market_id]}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        comparable_market_id: e.target.value,
                      })
                    }
                    description='Para hacer comparaciones de precios de venta.'
                  >
                    {[
                      { name: 'SHANGHAI', id: 'CNSHA', country: 'CHINA' },
                      { name: 'GUANGZHOU', id: 'CNCAN', country: 'CHINA' },
                    ].map((market) => (
                      <SelectItem
                        key={market.id}
                        value={market.id}
                      >{`${market.name}, ${market.country}`}</SelectItem>
                    ))}
                  </Select>

                  <div>
                    <Select
                      label='Moneda Local'
                      selectedKeys={
                        formData.local_currency ? [formData.local_currency] : []
                      }
                      onChange={(e) => {
                        const newCurrency = e.target.value.toUpperCase();
                        setFormData({
                          ...formData,
                          local_currency: newCurrency,
                          settlement_details: formData.settlement_details?.map(
                            (detail) => ({
                              ...detail,
                              local_currency: newCurrency,
                            })
                          ),
                        });
                        // Maintain a local snapshot when switching to non-USD
                        if (newCurrency !== 'USD') {
                          setFreightLocalSnapshot(
                            (formData.total_freight_usd || 0) *
                              (formData.exchange_rate || 1)
                          );
                        } else {
                          setFreightLocalSnapshot(null);
                        }
                      }}
                    >
                      {currencies.map((currency) => (
                        <SelectItem key={currency.value} value={currency.value}>
                          {currency.name}
                        </SelectItem>
                      ))}
                    </Select>
                    {formData.local_currency &&
                      formData.local_currency.toUpperCase() !== 'RMB' && (
                        <p className='text-xs text-warning-600 mt-1'>
                          Al no ser RMB, no se calcularan comparaciones vs
                          mercado para esta liquidacion.
                        </p>
                      )}
                  </div>
                  <Input
                    label='Incoterm Original'
                    value={formData.original_incoterm}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        original_incoterm: e.target.value.toUpperCase(),
                      })
                    }
                  />
                  <Autocomplete
                    label='Tipo de Negocio'
                    inputValue={formData.business_type}
                    onInputChange={(value) =>
                      setFormData({
                        ...formData,
                        business_type: (value || '').toUpperCase(),
                      })
                    }
                    onSelectionChange={(key) =>
                      setFormData({
                        ...formData,
                        business_type: (key?.toString() || '').toUpperCase(),
                      })
                    }
                    onBlur={() => {
                      const v = (formData.business_type || '')
                        .trim()
                        .toUpperCase();
                      if (v && !businessTypeOptions.includes(v)) {
                        setBusinessTypeOptions((prev) => [...prev, v].sort());
                      }
                    }}
                    allowsCustomValue
                    placeholder='Selecciona o escribe uno nuevo'
                    description='Por ejemplo: Mínimo Garantizado, Libre Consignación, En Firme, etc.'
                    defaultItems={businessTypeOptions.map((bt) => ({
                      key: bt,
                      name: bt,
                    }))}
                  >
                    {(item) => (
                      <AutocompleteItem key={item.key} value={item.key}>
                        {item.name}
                      </AutocompleteItem>
                    )}
                  </Autocomplete>
                  <Input
                    label='Orden de Venta'
                    value={formData.sale_order}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sale_order: e.target.value.toUpperCase(),
                      })
                    }
                    description='Útil si agrupas varias liquidaciones/contenedores en una sola orden de venta.'
                  />
                </div>
              </div>

              {/* Transporte Section */}
              <div>
                <h2 className='text-xl font-semibold mb-4'>Transporte</h2>
                {suggestedShipName && (
                  <Alert
                    variant={
                      suggestedShipName.includes('✅')
                        ? 'success'
                        : suggestedShipName.includes('❌')
                          ? 'danger'
                          : 'default'
                    }
                    className='mb-4'
                  >
                    {suggestedShipName}
                  </Alert>
                )}
                {shipmentTripSuggestion && (
                  <div className='p-4 rounded-lg mb-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200'>
                    <p className='text-sm font-semibold mb-2'>
                      Sugerencia encontrada en base de despachos
                    </p>
                    <div className='grid grid-cols-1 md:grid-cols-4 gap-2 text-sm'>
                      <span>
                        <strong>Nave:</strong>{' '}
                        {shipmentTripSuggestion.ship_name || 'No definido'}
                      </span>
                      <span>
                        <strong>ETD:</strong>{' '}
                        {formatTripSuggestionDate(
                          shipmentTripSuggestion.ship_etd
                        )}
                      </span>
                      <span>
                        <strong>ETA:</strong>{' '}
                        {formatTripSuggestionDate(
                          shipmentTripSuggestion.ship_eta
                        )}
                      </span>
                      <span>
                        <strong>Puerto destino:</strong>{' '}
                        {shipmentTripSuggestion.ship_destination_port ||
                          'No definido'}
                      </span>
                    </div>
                    <p className='text-xs text-blue-600 mt-2'>
                      Selecciona manualmente la nave y el viaje si corresponde,
                      esta es solo una referencia. Recuerda que las bases de
                      despachos muchas veces no tienen al día estos datos.
                    </p>
                  </div>
                )}
                <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
                  {/* Transport Type Selector - First Column */}
                  <TransportTypeSelector
                    value={transportType}
                    onChange={(newType) => {
                      setTransportType(newType);
                      setSelectedShipTripId(''); // Reset trip selection
                      setFormData({ ...formData, ship_trip_id: undefined });

                      // Auto-select ship based on transport type
                      if (newType === 'A') {
                        initializeManualTripData(
                          formData.date_sold || '',
                          formData.market_id || ''
                        );
                      } else if (newType === 'T') {
                        initializeManualTripData(
                          formData.date_sold || '',
                          formData.market_id || ''
                        );
                      } else {
                        // For MARITIMO, reset ship selection to show dropdown
                        setSelectedShipId('');
                      }
                    }}
                    //   description={suggestedShipName ? `${suggestedShipName}` : ''}
                  />

                  {/* Only show ship selection for MARITIMO */}
                  {transportType === 'M' && (
                    <span className='flex flex-col gap-2'>
                      <label className='text-sm font-medium text-default-600'>
                        Nave
                      </label>
                      <Autocomplete
                        selectedKey={selectedShipId}
                        onSelectionChange={(key) => {
                          const newShipId = key?.toString() || '';
                          setSelectedShipId(newShipId);
                          setSelectedShipTripId(''); // Reset trip selection
                          setFormData({ ...formData, ship_trip_id: undefined });
                        }}
                        isDisabled={!transportType}
                        placeholder={
                          !transportType
                            ? 'Selecciona primero el tipo de transporte'
                            : 'Busca una nave...'
                        }
                        allowsCustomValue={false}
                        defaultItems={getShipsByType(transportType)}
                      >
                        {(ship) => (
                          <AutocompleteItem key={ship.id} textValue={ship.name}>
                            {ship.name}
                          </AutocompleteItem>
                        )}
                      </Autocomplete>
                    </span>
                  )}

                  {/* Conditional rendering based on transport type */}
                  {requiresManualTrip() ? (
                    <>
                      {/* Manual Trip Creation Fields for A and T */}
                      <span className='flex flex-col gap-2'>
                        <label className='text-sm font-medium text-default-600'>
                          Fecha de Salida (ETD)
                        </label>
                        <Input
                          //   label='Fecha Embarque'
                          type='date'
                          value={manualTripData.fecha_embarque}
                          onChange={(e) =>
                            setManualTripData((prev) => ({
                              ...prev,
                              fecha_embarque: e.target.value,
                            }))
                          }
                          isDisabled={
                            !selectedShipId &&
                            transportType !== 'A' &&
                            transportType !== 'T'
                          }
                          placeholder={
                            !selectedShipId ? 'Selecciona primero una nave' : ''
                          }
                        />
                      </span>

                      <span className='flex flex-col gap-2'>
                        <label className='text-sm font-medium text-default-600'>
                          Fecha de Llegada (ETA)
                        </label>
                        <Input
                          //   label='Fecha Llegada'
                          type='date'
                          value={manualTripData.fecha_llegada}
                          onChange={(e) =>
                            setManualTripData((prev) => ({
                              ...prev,
                              fecha_llegada: e.target.value,
                            }))
                          }
                          isDisabled={
                            !selectedShipId &&
                            transportType !== 'A' &&
                            transportType !== 'T'
                          }
                          placeholder={
                            !selectedShipId ? 'Selecciona primero una nave' : ''
                          }
                        />
                      </span>
                      <span className='flex flex-col gap-2'>
                        <label className='text-sm font-medium text-default-600'>
                          Lugar de Arribo
                        </label>
                        <Autocomplete
                          selectedKey={manualTripData.destino_market_id}
                          onSelectionChange={(key) => {
                            const marketId = key?.toString() || '';
                            setManualTripData((prev) => ({
                              ...prev,
                              destino_market_id: marketId,
                            }));
                          }}
                          isDisabled={
                            !selectedShipId &&
                            transportType !== 'A' &&
                            transportType !== 'T'
                          }
                          placeholder={
                            !selectedShipId
                              ? 'Selecciona primero una nave'
                              : 'Busca puerto de destino...'
                          }
                          allowsCustomValue={false}
                          defaultItems={markets}
                        >
                          {(market) => (
                            <AutocompleteItem
                              key={market.id}
                              textValue={`${market.name}, ${market.country}`}
                            >
                              {`${market.name}, ${market.country}`}
                            </AutocompleteItem>
                          )}
                        </Autocomplete>
                      </span>
                    </>
                  ) : transportType === 'M' ? (
                    <span className='flex flex-col gap-2 col-span-2'>
                      <label className='text-sm font-medium text-default-600'>
                        Viaje
                      </label>

                      <Autocomplete
                        selectedKey={selectedShipTripId}
                        onSelectionChange={(key) => {
                          const newTripId = key?.toString() || '';
                          setSelectedShipTripId(newTripId);
                          setFormData({
                            ...formData,
                            ship_trip_id: newTripId
                              ? Number(newTripId)
                              : undefined,
                          });
                        }}
                        listboxProps={{
                          emptyContent:
                            'No se encontraron viajes para esta nave, temporada o fecha de venta',
                        }}
                        isDisabled={!selectedShipId}
                        defaultItems={getTripsByShip(
                          selectedShipId,
                          formData.season,
                          formData.date_sold
                        )}
                        description={
                          !formData.season &&
                          'Completa primero la temporada y fecha de venta para mostrarte los viajes'
                        }
                        placeholder={
                          !selectedShipId
                            ? 'Selecciona primero una nave'
                            : !formData.season
                              ? 'Completa primero la temporada y fecha de venta para mostrarte los viajes'
                              : 'Selecciona un viaje'
                        }
                        allowsCustomValue={false}
                      >
                        {(trip) => (
                          <AutocompleteItem
                            key={trip.id}
                            textValue={`ETD: ${formatDate(trip.etd, 'dd MMMM yyyy')}, ETA: ${formatDate(trip.eta, 'dd MMMM yyyy')}, Destino: ${trip.market?.name || 'No definido'}`}
                          >
                            <div className='flex flex-col'>
                              <span className='text-small'>
                                ETD: {formatDate(trip.etd, 'dd MMMM yyyy')},
                                ETA: {formatDate(trip.eta, 'dd MMMM yyyy')},
                                Destino: {trip.market?.name || 'No definido'}
                              </span>
                            </div>
                          </AutocompleteItem>
                        )}
                      </Autocomplete>
                    </span>
                  ) : null}
                </div>
              </div>

              {/* New comparison section here */}
              <div>
                <div className='flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4'>
                  <div>
                    <h2 className='text-xl font-semibold'>
                      Validación Packing List
                    </h2>
                    <p className='text-sm text-default-400'>
                      Comparamos lo liquidado con la base de despachos cargada
                      en Cherrymarket para asegurarnos de que las cantidades
                      coincidan.
                    </p>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Button
                      size='sm'
                      variant='flat'
                      onPress={handleSearchShipment}
                      isLoading={isFetchingShipment}
                    >
                      <span className='flex items-center gap-2'>
                        <Icon icon='tabler:search' />
                        Buscar por Número de Referencia
                      </span>
                    </Button>
                    <Button
                      size='sm'
                      variant='flat'
                      onPress={handleSearchContainer}
                      isLoading={isFetchingContainer}
                    >
                      <span className='flex items-center gap-2'>
                        <Icon icon='tabler:search' />
                        Buscar por Contenedor
                      </span>
                    </Button>
                  </div>
                </div>
                <div className='relative'>
                  {(isFetchingShipment || isFetchingContainer) && (
                    <div className='absolute inset-0 z-10 flex items-center justify-center bg-background/60'>
                      <Spinner label='Buscando despachos...' />
                    </div>
                  )}
                  {fetchShipmentError ? (
                    <Alert variant='danger'>{fetchShipmentError}</Alert>
                  ) : (
                    <>
                      {missingFormatCodes.length > 0 && (
                        <div className='mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950'>
                          <div className='mb-3 flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-200'>
                            <Icon icon='tabler:stack-2' />
                            <span>
                              Asigna formato a {missingFormatCodes.length}{' '}
                              código
                              {missingFormatCodes.length === 1 ? '' : 's'} de
                              embalaje sin mapeo.
                            </span>
                          </div>
                          <div className='grid grid-cols-1 gap-2 md:grid-cols-2'>
                            {missingFormatCodes.map((code) => (
                              <div
                                key={code}
                                className='flex flex-col gap-2 rounded-md border border-amber-200 bg-content1 p-2 dark:border-amber-800 sm:flex-row sm:items-center'
                              >
                                <span className='min-w-0 flex-1 truncate text-sm font-medium text-default-700'>
                                  {code}
                                </span>
                                <Select
                                  size='sm'
                                  aria-label={`Formato para ${code}`}
                                  placeholder='Formato'
                                  className='w-full sm:w-40'
                                  selectedKeys={
                                    inlineFormatSelections[code]
                                      ? [inlineFormatSelections[code]]
                                      : []
                                  }
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    setInlineFormatSelections((prev) => ({
                                      ...prev,
                                      [code]: value,
                                    }));
                                  }}
                                >
                                  {VALID_FORMATS.map((format) => (
                                    <SelectItem key={format} value={format}>
                                      {format}
                                    </SelectItem>
                                  ))}
                                </Select>
                                <Button
                                  size='sm'
                                  variant='flat'
                                  className='sm:w-24'
                                  isLoading={savingInlineFormatCode === code}
                                  isDisabled={!inlineFormatSelections[code]}
                                  onPress={() =>
                                    handleInlineFormatAssignment(code)
                                  }
                                >
                                  Asignar
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {comparisonData.length === 0 ? (
                        <p>No hay datos de comparación disponibles</p>
                      ) : (
                        <>
                          {isPackingListValidationOK && (
                            <Alert variant='success' className='mb-3'>
                              <span className='flex items-center gap-2'>
                                <Icon icon='tabler:check' /> Lo liquidado cuadra
                                con lo despachado.
                              </span>
                            </Alert>
                          )}
                          <Table aria-label='Comparación de cantidades despachadas vs liquidadas'>
                            <TableHeader>
                              <TableColumn>Producto</TableColumn>
                              <TableColumn>Liquidado</TableColumn>
                              <TableColumn>Despachado</TableColumn>
                            </TableHeader>
                            <TableBody>
                              {comparisonData.map((item, index) => (
                                <TableRow
                                  key={index}
                                  className={
                                    item.isMatched
                                      ? undefined
                                      : 'bg-rose-50 dark:bg-rose-950 text-rose-700 dark:text-rose-300'
                                  }
                                >
                                  <TableCell>
                                    <div className='flex items-center gap-2'>
                                      <span>{item.product}</span>
                                      <Chip
                                        size='sm'
                                        variant='flat'
                                        color={
                                          item.isMatched ? 'success' : 'danger'
                                        }
                                      >
                                        {item.isMatched
                                          ? 'Cuadrada'
                                          : 'No cuadrada'}
                                      </Chip>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    {item.settled.toLocaleString()}
                                  </TableCell>
                                  <TableCell>
                                    {item.shipped.toLocaleString()}
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow
                                key='comparison-total'
                                className='bg-default-100'
                              >
                                <TableCell className='font-semibold'>
                                  Total
                                </TableCell>
                                <TableCell className='font-semibold'>
                                  {comparisonTotals.settled.toLocaleString()}
                                </TableCell>
                                <TableCell className='font-semibold'>
                                  {comparisonTotals.shipped.toLocaleString()}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Sección Ingresos y Costos */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {/* Tabla de Ingresos */}
                <div className='col-span-1 md:col-span-2'>
                  <div className='flex justify-between items-center mb-4'>
                    <h2 className='text-xl font-semibold'>Ingresos</h2>
                    <div className='flex flex-col items-end gap-2'>
                      <div className='flex items-center gap-2'>
                        <Tooltip
                          content={
                            allowedLabels.length > 0
                              ? `Etiquetas oficiales: ${allowedLabels
                                  .slice(0, 6)
                                  .join(', ')}${
                                  allowedLabels.length > 6
                                    ? ` y ${allowedLabels.length - 6} más`
                                    : ''
                                }`
                              : 'Etiquetas oficiales: sin datos'
                          }
                        >
                          <span
                            className={`rounded-full px-3 py-1 text-xs font-medium ${labelStatusInfo.className}`}
                          >
                            {labelStatusInfo.text}
                          </span>
                        </Tooltip>
                        <Tooltip
                          content={
                            allowedVarieties.length > 0
                              ? `Variedades oficiales: ${allowedVarieties
                                  .slice(0, 6)
                                  .join(', ')}${
                                  allowedVarieties.length > 6
                                    ? ` y ${allowedVarieties.length - 6} más`
                                    : ''
                                }`
                              : 'Variedades oficiales: sin datos'
                          }
                        >
                          <span
                            className={`rounded-full px-3 py-1 text-xs font-medium ${varietyStatusInfo.className}`}
                          >
                            {varietyStatusInfo.text}
                          </span>
                        </Tooltip>
                        {detailValuesStatusInfo && (
                          <span
                            className={`rounded-full px-3 py-1 text-xs font-medium ${detailValuesStatusInfo.className}`}
                          >
                            {detailValuesStatusInfo.text}
                          </span>
                        )}
                        <Button
                          size='sm'
                          variant='flat'
                          onPress={() => setShowMarketComments((prev) => !prev)}
                          startContent={<Icon icon='tabler:message' />}
                          isDisabled={
                            isLoadingMarketComments ||
                            marketComments.length === 0
                          }
                          title={
                            marketComments.length === 0
                              ? 'No hay comentarios para mostrar'
                              : showMarketComments
                                ? 'Ocultar bitácora'
                                : 'Mostrar bitácora'
                          }
                        >
                          {showMarketComments
                            ? 'Ocultar Bitácora de Mercado'
                            : 'Mostrar Bitácora de Mercado'}
                          {marketComments.length > 0 && (
                            <span className='ml-2 inline-flex items-center justify-center rounded-full bg-background/60 px-2 py-0.5 text-xs font-semibold text-default-600'>
                              {marketComments.length}
                            </span>
                          )}
                        </Button>
                        {isManualUpload && (
                          <Tooltip
                            content={
                              isGrouped
                                ? 'Desagrupar y volver a las líneas originales'
                                : 'Agrupará las líneas por Variedad, Formato, Calibre, Color, Etiqueta, Categoria, Peso Bulto'
                            }
                          >
                            <Button
                              size='sm'
                              color={isGrouped ? 'primary' : 'default'}
                              variant='flat'
                              onPress={handleGroupByProduct}
                              startContent={
                                isGrouped ? (
                                  <Icon icon='tabler:stack-pop' />
                                ) : (
                                  <Icon icon='tabler:stack-2' />
                                )
                              }
                              isDisabled={
                                isAddingNewDetail ||
                                copyMode !== null ||
                                formData.settlement_details.length === 0
                              }
                              title={
                                isGrouped
                                  ? 'Desagrupar y volver a las líneas originales'
                                  : 'Agrupar detalles por producto (Variedad, Formato, Calibre, Color, Etiqueta, Categoria, Peso Bulto)'
                              }
                            >
                              {isGrouped
                                ? 'Desagrupar Líneas'
                                : 'Agrupar Líneas por Producto'}
                            </Button>
                          </Tooltip>
                        )}
                        <Button
                          size='sm'
                          variant='flat'
                          onPress={handleAddNewDetail}
                          isDisabled={isAddingNewDetail || copyMode !== null}
                          startContent={<Icon icon='tabler:plus' />}
                          title={
                            copyMode
                              ? 'No disponible en modo copia'
                              : 'Agregar nueva línea de detalle'
                          }
                        >
                          Agregar Línea de Detalle
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`grid gap-6 ${
                      showMarketComments
                        ? 'grid-cols-1 xl:grid-cols-[minmax(0,1fr)_24rem]'
                        : 'grid-cols-1'
                    }`}
                  >
                    <div>
                      <div className='border border-divider rounded-lg'>
                        <Table
                          removeWrapper
                          aria-label='Detalles de liquidación'
                          className='h-full'
                          classNames={{
                            base: 'h-full overflow-auto',
                            table: 'h-auto',
                            thead: 'bg-default-100 sticky top-0 z-20',
                            tbody: '',
                            tr: 'border-b border-divider',
                            th: 'bg-default-100 font-semibold',
                          }}
                        >
                          <TableHeader>
                            <TableColumn>ACCIONES</TableColumn>
                            <TableColumn className={copyMode ? '' : 'hidden'}>
                              SELECCIONAR
                            </TableColumn>
                            <TableColumn>PALLET</TableColumn>
                            <TableColumn>VARIEDAD</TableColumn>
                            <TableColumn>CALIBRE</TableColumn>
                            <TableColumn>COLOR</TableColumn>
                            <TableColumn>CODIGO EMBALAJE</TableColumn>
                            <TableColumn>FORMATO</TableColumn>
                            <TableColumn>ETIQUETA</TableColumn>
                            <TableColumn>CATEGORIA</TableColumn>
                            <TableColumn>PESO BULTO</TableColumn>
                            <TableColumn>BULTOS</TableColumn>
                            <TableColumn>KILOS</TableColumn>
                            <TableColumn>PRECIO BULTO</TableColumn>
                            <TableColumn>INGRESOS TOTALES</TableColumn>
                          </TableHeader>
                          <TableBody
                            emptyContent={'No hay detalles disponibles'}
                          >
                            {[
                              ...(isAddingNewDetail
                                ? [
                                    <TableRow
                                      key='new-detail'
                                      className='bg-blue-50 dark:bg-blue-950'
                                    >
                                      <TableCell>
                                        <div className='flex gap-1'>
                                          <Button
                                            isIconOnly
                                            size='sm'
                                            color='success'
                                            onClick={handleConfirmNewDetail}
                                          >
                                            <Icon icon='tabler:check' />
                                          </Button>
                                          <Button
                                            isIconOnly
                                            size='sm'
                                            color='danger'
                                            onClick={handleCancelNewDetail}
                                          >
                                            <Icon icon='tabler:x' />
                                          </Button>
                                        </div>
                                      </TableCell>
                                      <TableCell
                                        className={copyMode ? '' : 'hidden'}
                                      >
                                        -
                                      </TableCell>
                                      <TableCell>
                                        <Input
                                          size='sm'
                                          value={newDetailData.pallet || ''}
                                          onChange={(e) =>
                                            handleNewDetailFieldChange(
                                              'pallet',
                                              e.target.value.toUpperCase()
                                            )
                                          }
                                          className='min-w-[80px]'
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Input
                                          size='sm'
                                          value={newDetailData.variety || ''}
                                          onChange={(e) =>
                                            handleNewDetailFieldChange(
                                              'variety',
                                              e.target.value.toUpperCase()
                                            )
                                          }
                                          className='min-w-[120px]'
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Input
                                          size='sm'
                                          value={newDetailData.caliber || ''}
                                          onChange={(e) =>
                                            handleNewDetailFieldChange(
                                              'caliber',
                                              e.target.value.toUpperCase()
                                            )
                                          }
                                          className='min-w-[80px]'
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Input
                                          size='sm'
                                          value={newDetailData.color || ''}
                                          onChange={(e) =>
                                            handleNewDetailFieldChange(
                                              'color',
                                              e.target.value.toUpperCase()
                                            )
                                          }
                                          className='min-w-[80px]'
                                        />
                                      </TableCell>
                                      <TableCell>-</TableCell>
                                      <TableCell>
                                        <Select
                                          size='sm'
                                          selectedKeys={[
                                            newDetailData.format || 'CAJA',
                                          ]}
                                          onChange={(e) =>
                                            handleNewDetailFieldChange(
                                              'format',
                                              e.target.value
                                            )
                                          }
                                          className='min-w-[120px]'
                                        >
                                          {VALID_FORMATS.map((format) => (
                                            <SelectItem
                                              key={format}
                                              value={format}
                                            >
                                              {format}
                                            </SelectItem>
                                          ))}
                                        </Select>
                                      </TableCell>

                                      <TableCell>
                                        <Input
                                          size='sm'
                                          value={newDetailData.label || ''}
                                          onChange={(e) =>
                                            handleNewDetailFieldChange(
                                              'label',
                                              e.target.value.toUpperCase()
                                            )
                                          }
                                          className='min-w-[120px]'
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Input
                                          size='sm'
                                          value={newDetailData.category || ''}
                                          onChange={(e) =>
                                            handleNewDetailFieldChange(
                                              'category',
                                              e.target.value.toUpperCase()
                                            )
                                          }
                                          className='min-w-[80px]'
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Input
                                          size='sm'
                                          type='number'
                                          value={
                                            newDetailData.unit_weight?.toString() ||
                                            ''
                                          }
                                          onChange={(e) =>
                                            handleNewDetailFieldChange(
                                              'unit_weight',
                                              e.target.value
                                            )
                                          }
                                          className='min-w-[80px]'
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Input
                                          size='sm'
                                          type='number'
                                          value={
                                            newDetailData.quantity?.toString() ||
                                            ''
                                          }
                                          onChange={(e) =>
                                            handleNewDetailFieldChange(
                                              'quantity',
                                              e.target.value
                                            )
                                          }
                                          className='min-w-[80px]'
                                        />
                                      </TableCell>
                                      <TableCell>
                                        {newDetailData.kilos || 0}
                                      </TableCell>
                                      <TableCell>
                                        <Input
                                          size='sm'
                                          type='number'
                                          value={
                                            (
                                              (newDetailData.local_total_price ||
                                                0) /
                                              (newDetailData.quantity || 1)
                                            )?.toString() || ''
                                          }
                                          onChange={(e) => {
                                            const pricePerBulto =
                                              parseFloat(e.target.value) || 0;
                                            const totalPrice =
                                              pricePerBulto *
                                              (newDetailData.quantity || 1);
                                            handleNewDetailFieldChange(
                                              'local_total_price',
                                              totalPrice.toString()
                                            );
                                          }}
                                          className='min-w-[100px]'
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <div className='font-medium text-green-600 dark:text-green-400'>
                                          {(
                                            newDetailData.local_total_price || 0
                                          ).toLocaleString()}{' '}
                                          {formData.local_currency}
                                        </div>
                                      </TableCell>
                                    </TableRow>,
                                  ]
                                : []),

                              ...(formData.settlement_details || []).map(
                                (detail) => (
                                  <TableRow
                                    key={detail.id}
                                    className={
                                      highlightedRowId === detail.id
                                        ? 'bg-green-100 dark:bg-green-950 transition-colors duration-500'
                                        : copyMode
                                          ? `cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-950 ${selectedRowsForCopy.has(detail.id!) ? 'bg-blue-100 dark:bg-blue-900 border-l-4 border-blue-500' : ''}${
                                              (copyMode.field === 'caliber' &&
                                                detail.caliber ===
                                                  copyMode.value) ||
                                              (copyMode.field === 'color' &&
                                                detail.color ===
                                                  copyMode.value) ||
                                              (copyMode.field === 'label' &&
                                                detail.label ===
                                                  copyMode.value) ||
                                              (copyMode.field === 'variety' &&
                                                detail.variety ===
                                                  copyMode.value) ||
                                              (copyMode.field === 'format' &&
                                                detail.format ===
                                                  copyMode.value) ||
                                              (copyMode.field === 'category' &&
                                                detail.category ===
                                                  copyMode.value)
                                                ? ' opacity-50 cursor-not-allowed'
                                                : ''
                                            }`
                                          : ''
                                    }
                                    onClick={
                                      copyMode
                                        ? (e) => {
                                            // Only handle row clicks if not clicking on interactive elements
                                            if (
                                              e.target instanceof HTMLElement &&
                                              !e.target.closest('button') &&
                                              !e.target.closest('input') &&
                                              !e.target.closest('select') &&
                                              !e.target.closest(
                                                '[role="checkbox"]'
                                              )
                                            ) {
                                              const isCurrentlySelected =
                                                selectedRowsForCopy.has(
                                                  detail.id!
                                                );
                                              const isDisabled =
                                                (copyMode.field === 'caliber' &&
                                                  detail.caliber ===
                                                    copyMode.value) ||
                                                (copyMode.field === 'color' &&
                                                  detail.color ===
                                                    copyMode.value) ||
                                                (copyMode.field === 'label' &&
                                                  detail.label ===
                                                    copyMode.value) ||
                                                (copyMode.field === 'variety' &&
                                                  detail.variety ===
                                                    copyMode.value) ||
                                                (copyMode.field === 'format' &&
                                                  detail.format ===
                                                    copyMode.value) ||
                                                (copyMode.field ===
                                                  'category' &&
                                                  detail.category ===
                                                    copyMode.value);

                                              if (!isDisabled) {
                                                handleRowSelectionForCopy(
                                                  detail.id!,
                                                  !isCurrentlySelected
                                                );
                                              } else {
                                                toast.info(
                                                  'Esta fila ya tiene el mismo valor'
                                                );
                                              }
                                            }
                                          }
                                        : undefined
                                    }
                                  >
                                    <TableCell>
                                      <div className='flex gap-1'>
                                        <Button
                                          isIconOnly
                                          size='sm'
                                          variant='light'
                                          color='primary'
                                          onClick={() =>
                                            handleDeleteDetail(detail.id!)
                                          }
                                          isDisabled={
                                            loading ||
                                            (formData.settlement_details
                                              ?.length || 0) <= 1 ||
                                            copyMode !== null
                                          }
                                          title={
                                            copyMode
                                              ? 'No disponible en modo copia'
                                              : (formData.settlement_details
                                                    ?.length || 0) <= 1
                                                ? 'No se puede eliminar el único detalle'
                                                : 'Eliminar detalle'
                                          }
                                        >
                                          <Icon icon='tabler:x' />
                                        </Button>
                                        <Button
                                          // isIconOnly
                                          size='sm'
                                          variant='light'
                                          color='primary'
                                          onClick={() =>
                                            handleDuplicateDetail(detail.id!)
                                          }
                                          isDisabled={
                                            loading ||
                                            isAddingNewDetail ||
                                            copyMode !== null
                                          }
                                          title={
                                            copyMode
                                              ? 'No disponible en modo copia'
                                              : isAddingNewDetail
                                                ? 'Complete el detalle actual antes de duplicar'
                                                : 'Duplicar detalle'
                                          }
                                        >
                                          <Icon icon='tabler:copy' /> Duplicar
                                        </Button>
                                      </div>
                                    </TableCell>
                                    <TableCell
                                      className={copyMode ? '' : 'hidden'}
                                    >
                                      {copyMode && (
                                        <Checkbox
                                          size='sm'
                                          isSelected={selectedRowsForCopy.has(
                                            detail.id!
                                          )}
                                          onValueChange={(isSelected) =>
                                            handleRowSelectionForCopy(
                                              detail.id!,
                                              isSelected
                                            )
                                          }
                                          isDisabled={
                                            (copyMode.field === 'caliber' &&
                                              detail.caliber ===
                                                copyMode.value) ||
                                            (copyMode.field === 'color' &&
                                              detail.color ===
                                                copyMode.value) ||
                                            (copyMode.field === 'label' &&
                                              detail.label ===
                                                copyMode.value) ||
                                            (copyMode.field === 'variety' &&
                                              detail.variety ===
                                                copyMode.value) ||
                                            (copyMode.field === 'format' &&
                                              detail.format ===
                                                copyMode.value) ||
                                            (copyMode.field === 'category' &&
                                              detail.category ===
                                                copyMode.value)
                                          }
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {renderEditableCell(
                                        detail.id!,
                                        'pallet',
                                        detail.pallet || ''
                                      )}
                                    </TableCell>
                                    <TableCell
                                      className={
                                        isVarietyUnknown(detail.variety)
                                          ? 'bg-red-50 dark:bg-red-950'
                                          : undefined
                                      }
                                    >
                                      {renderEditableCell(
                                        detail.id!,
                                        'variety',
                                        detail.variety || ''
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {renderEditableCell(
                                        detail.id!,
                                        'caliber',
                                        detail.caliber || ''
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {renderEditableCell(
                                        detail.id!,
                                        'color',
                                        detail.color || ''
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {isManualUpload ? (
                                        <p className='text-xs text-default-400'>
                                          {detail.box_type}
                                        </p>
                                      ) : (
                                        <p className='text-xs text-default-400'>
                                          No necesario
                                        </p>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {renderEditableCell(
                                        detail.id!,
                                        'format',
                                        detail.format || ''
                                      )}
                                    </TableCell>

                                    <TableCell
                                      className={
                                        isLabelUnknown(detail.label)
                                          ? 'bg-red-50 dark:bg-red-950'
                                          : undefined
                                      }
                                    >
                                      {renderEditableCell(
                                        detail.id!,
                                        'label',
                                        detail.label || ''
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {renderEditableCell(
                                        detail.id!,
                                        'category',
                                        detail.category || ''
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {renderEditableCell(
                                        detail.id!,
                                        'unit_weight',
                                        detail.unit_weight?.toString() || ''
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {renderEditableCell(
                                        detail.id!,
                                        'quantity',
                                        detail.quantity?.toString() || ''
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <div className='font-medium'>
                                        {(detail.kilos || 0).toLocaleString()}{' '}
                                        kg
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      {renderEditableCell(
                                        detail.id!,
                                        'price_per_bulto',
                                        (
                                          (detail.local_total_price || 0) /
                                          (detail.quantity || 1)
                                        )?.toString() || ''
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <div className='font-medium text-green-600 dark:text-green-400'>
                                        {(
                                          detail.local_total_price || 0
                                        ).toLocaleString()}{' '}
                                        {detail.local_currency}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )
                              ),
                            ]}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Floating Copy Confirmation Button */}
                      {copyMode && (
                        <div className='fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-background border border-divider rounded-lg shadow-lg p-4'>
                          <div className='flex items-center gap-4'>
                            <div className='text-sm'>
                              <span className='font-medium'>Copiando:</span> "
                              {copyMode.value}"
                              <span className='text-default-400 ml-2'>
                                en{' '}
                                {copyMode.field === 'caliber'
                                  ? 'CALIBRE'
                                  : copyMode.field === 'color'
                                    ? 'COLOR'
                                    : copyMode.field === 'label'
                                      ? 'ETIQUETA'
                                      : copyMode.field === 'variety'
                                        ? 'VARIEDAD'
                                        : copyMode.field === 'format'
                                          ? 'FORMATO'
                                          : copyMode.field === 'category'
                                            ? 'CATEGORIA'
                                            : ''}
                              </span>
                            </div>
                            <div className='text-sm font-medium text-blue-600'>
                              {selectedRowsForCopy.size} filas seleccionadas
                            </div>
                            <div className='flex gap-2'>
                              <Button
                                size='sm'
                                variant='flat'
                                onClick={() => {
                                  setSelectedRowsForCopy(
                                    new Set(
                                      formData.settlement_details.map(
                                        (detail) => detail.id!
                                      )
                                    )
                                  );
                                }}
                                isDisabled={
                                  selectedRowsForCopy.size ===
                                  formData.settlement_details.length
                                }
                              >
                                Seleccionar todas las filas
                              </Button>
                              <Button
                                size='sm'
                                color='primary'
                                onClick={handleConfirmCopyToRows}
                                isDisabled={selectedRowsForCopy.size === 0}
                              >
                                Confirmar
                              </Button>
                              <Button
                                size='sm'
                                variant='light'
                                onClick={handleCancelCopyMode}
                              >
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Resumen */}
                      <div className='grid grid-cols-3 gap-4 mt-4 p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg'>
                        <div className='text-center'>
                          <div className='text-2xl font-bold text-blue-600'>
                            {formData.settlement_details
                              .reduce(
                                (sum, detail) => sum + (detail.quantity || 0),
                                0
                              )
                              .toLocaleString()}
                          </div>
                          <div className='text-sm text-default-500'>
                            Total Bultos
                          </div>
                        </div>
                        <div className='text-center'>
                          <div className='text-2xl font-bold text-blue-600'>
                            {formData.settlement_details
                              .reduce(
                                (sum, detail) => sum + (detail.kilos || 0),
                                0
                              )
                              .toLocaleString()}{' '}
                            kg
                          </div>
                          <div className='text-sm text-default-500'>
                            Total Kilos
                          </div>
                        </div>
                        <div className='text-center'>
                          <div className='text-2xl font-bold text-green-600 dark:text-green-400'>
                            {formData.total_revenue_local?.toLocaleString()}{' '}
                            {formData.local_currency}
                          </div>
                          <div className='text-sm text-default-500'>
                            Total Ingresos
                          </div>
                        </div>
                      </div>
                    </div>
                    {showMarketComments && (
                      <div className='rounded-lg border border-divider bg-default-100/50 p-4'>
                        <div className='mb-3'>
                          <div className='text-sm font-semibold text-default-600'>
                            Bitácora de Mercado
                          </div>
                        </div>
                        <div className='grid gap-3'>
                          {isLoadingMarketComments && (
                            <LoadingState label='Cargando...' />
                          )}
                          {marketCommentsError && (
                            <Alert variant='danger'>
                              {marketCommentsError}
                            </Alert>
                          )}
                          {!isLoadingMarketComments &&
                            !marketCommentsError &&
                            marketComments.map((comment) => {
                              const referenceMatch = Boolean(
                                normalizeMatchValue(comment.exporter_ref) &&
                                  normalizeMatchValue(comment.exporter_ref) ===
                                    normalizeMatchValue(
                                      formData.reference_number
                                    )
                              );
                              const containerMatch = Boolean(
                                normalizeMatchValue(comment.container_number) &&
                                  normalizeMatchValue(
                                    comment.container_number
                                  ) ===
                                    normalizeMatchValue(
                                      formData.container_number
                                    )
                              );
                              const dateLabel =
                                comment.date_to &&
                                comment.date_to !== comment.date_from
                                  ? `${formatDate(comment.date_from, 'dd MMM yyyy')} → ${formatDate(comment.date_to, 'dd MMM yyyy')}`
                                  : formatDate(
                                      comment.date_from,
                                      'dd MMM yyyy'
                                    );
                              const createdAt =
                                comment.created_at?.split('T')[0] || '';
                              const priceLabel =
                                typeof comment.unit_price === 'number'
                                  ? `${comment.unit_price.toFixed(2)} ${comment.local_currency || ''}`.trim()
                                  : '—';
                              return (
                                <Card key={comment.id} shadow='sm'>
                                  <CardBody>
                                    <div className='flex w-full items-center justify-between text-xs text-default-500'>
                                      <div className='font-medium truncate'>
                                        {dateLabel}
                                      </div>
                                      <div className='text-xs text-default-400'>
                                        Mercado:{' '}
                                        {formatMarketValue(comment.market_id)}
                                      </div>
                                    </div>
                                    <div className='mt-1 text-xs text-default-400'>
                                      Coincidencia:{' '}
                                      {referenceMatch && containerMatch
                                        ? 'Referencia y Contenedor'
                                        : referenceMatch
                                          ? 'Referencia'
                                          : containerMatch
                                            ? 'Contenedor'
                                            : '—'}
                                    </div>
                                    {comment.content ? (
                                      <div className='mt-2 text-sm whitespace-pre-wrap'>
                                        {comment.content}
                                      </div>
                                    ) : (
                                      <div className='mt-2 text-sm italic text-default-500'>
                                        (Sin contenido)
                                      </div>
                                    )}
                                    <div className='mt-2 rounded-md border border-default-200 bg-default-50 p-2'>
                                      <div className='grid grid-cols-2 gap-x-4 gap-y-1 text-xs'>
                                        <div>
                                          <span className='text-default-500'>
                                            Especie ID:{' '}
                                          </span>
                                          <span className='font-medium'>
                                            {formatMarketValue(
                                              comment.species_id ??
                                                formData.species_id
                                            )}
                                          </span>
                                        </div>
                                        <div>
                                          <span className='text-default-500'>
                                            Temporada:{' '}
                                          </span>
                                          <span className='font-medium'>
                                            {formatMarketValue(
                                              comment.season ?? formData.season
                                            )}
                                          </span>
                                        </div>
                                        <div>
                                          <span className='text-default-500'>
                                            Variedad:{' '}
                                          </span>
                                          <span className='font-medium'>
                                            {formatMarketValue(comment.variety)}
                                          </span>
                                        </div>
                                        <div>
                                          <span className='text-default-500'>
                                            Calibre:{' '}
                                          </span>
                                          <span className='font-medium'>
                                            {formatMarketValue(comment.caliber)}
                                          </span>
                                        </div>
                                        <div>
                                          <span className='text-default-500'>
                                            Cliente:{' '}
                                          </span>
                                          <span className='font-medium'>
                                            {formatMarketValue(comment.seller)}
                                          </span>
                                        </div>
                                        <div>
                                          <span className='text-default-500'>
                                            Etiqueta:{' '}
                                          </span>
                                          <span className='font-medium'>
                                            {formatMarketValue(comment.label)}
                                          </span>
                                        </div>
                                        <div>
                                          <span className='text-default-500'>
                                            Referencia:{' '}
                                          </span>
                                          <span className='font-medium'>
                                            {formatMarketValue(
                                              comment.exporter_ref
                                            )}
                                          </span>
                                        </div>
                                        <div>
                                          <span className='text-default-500'>
                                            Contenedor:{' '}
                                          </span>
                                          <span className='font-medium'>
                                            {formatMarketValue(
                                              comment.container_number
                                            )}
                                          </span>
                                        </div>
                                        <div>
                                          <span className='text-default-500'>
                                            Cantidad de bultos:{' '}
                                          </span>
                                          <span className='font-medium'>
                                            {formatMarketValue(
                                              comment.unit_count
                                            )}
                                          </span>
                                        </div>
                                        <div>
                                          <span className='text-default-500'>
                                            Peso bulto:{' '}
                                          </span>
                                          <span className='font-medium'>
                                            {typeof comment.unit_weight ===
                                            'number'
                                              ? `${comment.unit_weight} kg`
                                              : '—'}
                                          </span>
                                        </div>
                                        <div>
                                          <span className='text-default-500'>
                                            Precio bulto:{' '}
                                          </span>
                                          <span className='font-medium'>
                                            {priceLabel}
                                          </span>
                                        </div>
                                        <div>
                                          <span className='text-default-500'>
                                            Moneda:{' '}
                                          </span>
                                          <span className='font-medium'>
                                            {formatMarketValue(
                                              comment.local_currency
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    {comment.attachments &&
                                      comment.attachments.filter(
                                        (attachment) => !attachment.deleted_at
                                      ).length > 0 && (
                                        <div className='mt-2 grid grid-cols-3 gap-2'>
                                          {comment.attachments
                                            .filter(
                                              (attachment) =>
                                                !attachment.deleted_at
                                            )
                                            .map((attachment) => (
                                              <button
                                                key={attachment.id}
                                                type='button'
                                                className='relative group h-20 w-20 rounded border overflow-hidden'
                                                title='Ver adjunto'
                                                onClick={async () => {
                                                  try {
                                                    if (attachment.is_image) {
                                                      const url =
                                                        marketCommentImageUrls[
                                                          attachment.id
                                                        ] ||
                                                        (await getMarketCommentAttachmentSignedUrl(
                                                          attachment.bucket,
                                                          attachment.path,
                                                          300
                                                        ));
                                                      window.open(
                                                        url,
                                                        '_blank'
                                                      );
                                                    } else {
                                                      const url =
                                                        await getMarketCommentAttachmentSignedUrl(
                                                          attachment.bucket,
                                                          attachment.path,
                                                          120
                                                        );
                                                      window.open(
                                                        url,
                                                        '_blank'
                                                      );
                                                    }
                                                  } catch {}
                                                }}
                                              >
                                                {attachment.is_image ? (
                                                  marketCommentImageUrls[
                                                    attachment.id
                                                  ] ? (
                                                    <img
                                                      src={
                                                        marketCommentImageUrls[
                                                          attachment.id
                                                        ]
                                                      }
                                                      alt={attachment.name}
                                                      className='h-full w-full object-cover'
                                                    />
                                                  ) : (
                                                    <div className='h-full w-full flex items-center justify-center text-[10px] bg-default-100'>
                                                      IMG
                                                    </div>
                                                  )
                                                ) : (
                                                  <div className='h-full w-full flex items-center justify-center text-[10px] bg-default-100'>
                                                    {attachment.name
                                                      ?.split('.')
                                                      .pop()
                                                      ?.toUpperCase() || 'FILE'}
                                                  </div>
                                                )}
                                                <div className='absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs flex items-center justify-center'>
                                                  Ver
                                                </div>
                                              </button>
                                            ))}
                                        </div>
                                      )}
                                    <Divider className='my-2' />
                                    <div className='text-xs text-default-400'>
                                      Creado el{' '}
                                      {createdAt
                                        ? formatDate(createdAt, 'dd MMM yyyy')
                                        : '—'}
                                    </div>
                                  </CardBody>
                                </Card>
                              );
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Resumen de Costos */}
                <div className='col-span-2'>
                  <div className='mb-4 flex flex-wrap items-center justify-between gap-2'>
                    <div className='flex flex-wrap items-center gap-3'>
                      <h2 className='text-xl font-semibold'>Costos</h2>
                      {exporterCostsAvailable && (
                        <CostStandardToggle
                          value={costStandard}
                          onChange={setCostStandard}
                        />
                      )}
                    </div>
                    {reviewCostLines.length > 0 && (
                      <div className='flex items-center gap-2'>
                        {costMappingsReady &&
                          resolvedCostLines.some(
                            (line) => line.mappingStatus === 'new'
                          ) && (
                            <Chip size='sm' color='warning' variant='flat'>
                              {
                                resolvedCostLines.filter(
                                  (line) => line.mappingStatus === 'new'
                                ).length
                              }{' '}
                              conceptos nuevos
                            </Chip>
                          )}
                        <Button
                          size='sm'
                          variant='light'
                          color='primary'
                          onPress={() => setCostEditorCategory('all')}
                        >
                          Editar costos originales · {reviewCostLines.length}
                        </Button>
                      </div>
                    )}
                  </div>
                  {showExporterCosts && (
                    <div className='space-y-4 p-4 border border-divider rounded-lg bg-default-100'>
                      <ExporterCostSummary
                        onCategoryPress={(categoryId) => {
                          setExporterCostEditorFilter(categoryId);
                          setCostEditorCategory('all');
                        }}
                        exchangeRateField={renderEditableCostField(
                          'exchange_rate',
                          formData.exchange_rate,
                          ''
                        )}
                        lines={reviewCostLines}
                        categories={receiverExporterCosts.categories}
                        mappings={receiverExporterCosts.mappings}
                        localCurrency={formData.local_currency || ''}
                        exchangeRate={Number(formData.exchange_rate) || 1}
                        revenueLocal={formData.total_revenue_local || 0}
                        fmCostsLocal={
                          (formData.total_commission_local || 0) +
                          (formData.total_tax_local || 0) +
                          (formData.total_other_costs_local || 0) +
                          (formData.total_repacking_costs_local || 0) +
                          (formData.total_freight_usd || 0) *
                            (formData.local_currency?.toUpperCase() === 'USD'
                              ? 1
                              : Number(formData.exchange_rate) || 1)
                        }
                      />
                    </div>
                  )}
                  <div
                    className={`space-y-4 p-4 border border-divider rounded-lg bg-default-100 ${
                      showExporterCosts ? 'hidden' : ''
                    }`}
                  >
                    {/* Total Ingresos */}
                    <div className='flex justify-between items-center py-2 border-b border-divider'>
                      <span className='font-semibold text-green-600 dark:text-green-400'>
                        Total Ingresos:
                      </span>
                      <span className='font-bold text-green-600 dark:text-green-400 flex items-baseline gap-2'>
                        <span>
                          {formData.total_revenue_local?.toLocaleString()}{' '}
                          {formData.local_currency}
                        </span>
                        {formData.local_currency?.toUpperCase() !== 'USD' &&
                          (formData.exchange_rate || 0) > 0 && (
                            <span className='text-xs text-default-400 font-normal'>
                              (≈{' '}
                              {(
                                (formData.total_revenue_local || 0) /
                                (formData.exchange_rate || 1)
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{' '}
                              USD)
                            </span>
                          )}
                      </span>
                    </div>

                    {/* <div className="text-xs text-default-500 italic mb-3">
                    💡 Los costos se distribuyen automáticamente:
                    <br />• Comisión y impuestos: proporcional por ingresos
                    <br />• Otros costos y flete: proporcional por kilos
                  </div> */}

                    {/* Comisión */}
                    <div className='flex items-center gap-2'>
                      <label className='text-sm font-medium text-red-600 dark:text-red-400 w-20 flex-shrink-0'>
                        Comisión:
                      </label>
                      {renderEditableCostField(
                        'commission',
                        formData.total_commission_local,
                        formData.local_currency || ''
                      )}
                      <span className='text-xs text-default-400 ml-2'>
                        (
                        {(
                          ((formData.total_commission_local || 0) /
                            (formData.total_revenue_local || 1)) *
                          100
                        ).toFixed(2)}
                        %)
                      </span>
                      {formData.local_currency?.toUpperCase() !== 'USD' &&
                        (formData.exchange_rate || 0) > 0 && (
                          <span className='text-xs text-default-400 ml-2'>
                            ≈{' '}
                            {(
                              (formData.total_commission_local || 0) /
                              (formData.exchange_rate || 1)
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{' '}
                            USD
                          </span>
                        )}
                    </div>

                    {/* Impuestos */}
                    <div className='flex items-center gap-2'>
                      <label className='text-sm font-medium text-red-600 dark:text-red-400 w-20 flex-shrink-0'>
                        Impuestos:
                      </label>
                      {renderEditableCostField(
                        'tax',
                        formData.total_tax_local,
                        formData.local_currency || ''
                      )}
                      <span className='text-xs text-default-400 ml-2'>
                        (
                        {(
                          ((formData.total_tax_local || 0) /
                            (formData.total_revenue_local || 1)) *
                          100
                        ).toFixed(2)}
                        %)
                      </span>
                      {formData.local_currency?.toUpperCase() !== 'USD' &&
                        (formData.exchange_rate || 0) > 0 && (
                          <span className='text-xs text-default-400 ml-2'>
                            ≈{' '}
                            {(
                              (formData.total_tax_local || 0) /
                              (formData.exchange_rate || 1)
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{' '}
                            USD
                          </span>
                        )}
                    </div>

                    {/* Repacking */}
                    <div className='flex items-center gap-2'>
                      <label className='text-sm font-medium text-red-600 dark:text-red-400 w-20 flex-shrink-0'>
                        Repacking:
                      </label>
                      {renderEditableCostField(
                        'repacking',
                        formData.total_repacking_costs_local,
                        formData.local_currency || ''
                      )}
                      <span className='text-xs text-default-400 ml-2'>
                        (
                        {(formData.settlement_details.reduce(
                          (sum, detail) => sum + (detail.kilos || 0),
                          0
                        ) > 0
                          ? (formData.total_repacking_costs_local || 0) /
                            formData.settlement_details.reduce(
                              (sum, detail) => sum + (detail.kilos || 0),
                              0
                            )
                          : 0
                        ).toFixed(3)}{' '}
                        {formData.local_currency}/kg)
                      </span>
                      {formData.local_currency?.toUpperCase() !== 'USD' &&
                        (formData.exchange_rate || 0) > 0 && (
                          <span className='text-xs text-default-400 ml-2'>
                            ≈{' '}
                            {(
                              (formData.total_repacking_costs_local || 0) /
                              (formData.exchange_rate || 1)
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{' '}
                            USD
                          </span>
                        )}
                    </div>

                    {/* Otros Costos */}
                    <div className='flex items-center gap-2'>
                      <label className='text-sm font-medium text-red-600 dark:text-red-400 w-20 flex-shrink-0'>
                        Otros:
                      </label>
                      {renderEditableCostField(
                        'otros',
                        formData.total_other_costs_local,
                        formData.local_currency || ''
                      )}
                      <span className='text-xs text-default-400 ml-2'>
                        (
                        {(formData.settlement_details.reduce(
                          (sum, detail) => sum + (detail.kilos || 0),
                          0
                        ) > 0
                          ? (formData.total_other_costs_local || 0) /
                            formData.settlement_details.reduce(
                              (sum, detail) => sum + (detail.kilos || 0),
                              0
                            )
                          : 0
                        ).toFixed(3)}{' '}
                        {formData.local_currency}/kg)
                      </span>
                      {formData.local_currency?.toUpperCase() !== 'USD' &&
                        (formData.exchange_rate || 0) > 0 && (
                          <span className='text-xs text-default-400 ml-2'>
                            ≈{' '}
                            {(
                              (formData.total_other_costs_local || 0) /
                              (formData.exchange_rate || 1)
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{' '}
                            USD
                          </span>
                        )}
                    </div>

                    {/* Total Costos */}
                    <div className='flex justify-between items-center py-2 border-t border-divider'>
                      <span className='font-semibold text-red-600 dark:text-red-400'>
                        Total Costos:
                      </span>
                      <span className='font-bold text-red-600 dark:text-red-400 flex items-baseline gap-2'>
                        <span>
                          {(
                            (formData.total_commission_local || 0) +
                            (formData.total_tax_local || 0) +
                            (formData.total_other_costs_local || 0) +
                            (formData.total_repacking_costs_local || 0)
                          ).toLocaleString()}{' '}
                          {formData.local_currency}
                        </span>
                        {formData.local_currency?.toUpperCase() !== 'USD' &&
                          (formData.exchange_rate || 0) > 0 && (
                            <span className='text-xs text-default-400 font-normal'>
                              (≈{' '}
                              {(
                                ((formData.total_commission_local || 0) +
                                  (formData.total_tax_local || 0) +
                                  (formData.total_other_costs_local || 0) +
                                  (formData.total_repacking_costs_local || 0)) /
                                (formData.exchange_rate || 1)
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{' '}
                              USD)
                            </span>
                          )}
                      </span>
                    </div>

                    {/* Subtotal */}
                    <div className='flex justify-between items-center py-2 border-t border-divider'>
                      <span className='font-semibold'>
                        Subtotal Moneda Local:
                      </span>
                      <span className='font-bold'>
                        {(
                          (formData.total_revenue_local || 0) -
                          (formData.total_commission_local || 0) -
                          (formData.total_tax_local || 0) -
                          (formData.total_other_costs_local || 0) -
                          (formData.total_repacking_costs_local || 0)
                        ).toLocaleString()}{' '}
                        {formData.local_currency}
                      </span>
                    </div>

                    {/* Tipo de Cambio */}
                    <div className='flex items-center gap-2'>
                      <label className='text-sm font-medium w-20 flex-shrink-0'>
                        Tipo de Cambio:
                      </label>
                      {renderEditableCostField(
                        'exchange_rate',
                        formData.exchange_rate,
                        ''
                      )}
                    </div>

                    {/* Exchange Rate Warning - When rate is 1 (local currency settlement) */}
                    {formData.exchange_rate === 1 &&
                      formData.local_currency?.toUpperCase() !== 'USD' && (
                        <div className='p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200 text-sm'>
                          <p className='font-semibold mb-1'>
                            ⚠️ Tipo de cambio a USD requerido
                          </p>
                          <p className='mb-2'>
                            No encontramos un tipo de cambio a USD en la
                            liquidación. Esto puede deberse a que te liquidaron
                            en moneda local. De todas formas, requerimos al
                            menos un tipo de cambio a USD referencial para hacer
                            las comparativas entre tus clientes y otras
                            exportadoras.
                          </p>
                          <p className='text-sm'>
                            💡 <strong>Recuerda:</strong> Actualizar también el
                            flete que probablemente se leyó en moneda local y
                            hay que traspasarlo a USD.
                          </p>
                        </div>
                      )}

                    {/* Subtotal USD */}
                    <div className='flex justify-between items-center py-2 border-t border-divider'>
                      <span className='font-semibold'>Subtotal USD:</span>
                      <span className='font-bold'>
                        {(
                          ((formData.total_revenue_local || 0) -
                            (formData.total_commission_local || 0) -
                            (formData.total_tax_local || 0) -
                            (formData.total_other_costs_local || 0) -
                            (formData.total_repacking_costs_local || 0)) /
                          (formData.exchange_rate || 1)
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        USD
                      </span>
                    </div>

                    {/* Flete USD */}
                    <div className='flex items-center gap-2'>
                      <label className='text-sm font-medium text-blue-600 w-20 flex-shrink-0'>
                        Flete:
                      </label>
                      {renderEditableCostField(
                        'freight',
                        formData.total_freight_usd,
                        'USD'
                      )}
                      <span className='text-xs text-default-400 ml-2'>
                        (
                        {(formData.settlement_details.reduce(
                          (sum, detail) => sum + (detail.kilos || 0),
                          0
                        ) > 0
                          ? (formData.total_freight_usd || 0) /
                            formData.settlement_details.reduce(
                              (sum, detail) => sum + (detail.kilos || 0),
                              0
                            )
                          : 0
                        ).toFixed(3)}{' '}
                        USD/kg)
                      </span>
                      {formData.local_currency?.toUpperCase() !== 'USD' &&
                        (formData.exchange_rate || 0) > 0 && (
                          <span className='text-xs text-default-400 ml-2'>
                            ≈{' '}
                            {(
                              (formData.total_freight_usd || 0) *
                              (formData.exchange_rate || 1)
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{' '}
                            {formData.local_currency}
                          </span>
                        )}
                    </div>

                    {/* FOB Final */}
                    <div className='flex justify-between items-center py-2 border-t-2 border-default-400 bg-blue-50 dark:bg-blue-950 px-2 rounded'>
                      <span className='font-bold text-blue-700 dark:text-blue-300'>
                        FOB Total:
                      </span>
                      <span className='font-bold text-blue-700 dark:text-blue-300 text-lg flex items-baseline gap-2'>
                        <span>
                          {(
                            ((formData.total_revenue_local || 0) -
                              (formData.total_commission_local || 0) -
                              (formData.total_tax_local || 0) -
                              (formData.total_other_costs_local || 0) -
                              (formData.total_repacking_costs_local || 0)) /
                              (formData.exchange_rate || 1) -
                            (formData.total_freight_usd || 0)
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{' '}
                          USD
                        </span>
                        {formData.local_currency?.toUpperCase() !== 'USD' &&
                          (formData.exchange_rate || 0) > 0 && (
                            <span className='text-sm text-default-500 font-normal'>
                              (≈{' '}
                              {(
                                (formData.total_revenue_local || 0) -
                                (formData.total_commission_local || 0) -
                                (formData.total_tax_local || 0) -
                                (formData.total_other_costs_local || 0) -
                                (formData.total_repacking_costs_local || 0) -
                                (formData.total_freight_usd || 0) *
                                  (formData.exchange_rate || 1)
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{' '}
                              {formData.local_currency})
                            </span>
                          )}
                      </span>
                    </div>

                    {/* <div className="flex justify-between items-center py-2 border-t border-divider bg-yellow-50 dark:bg-yellow-950 px-2 rounded">
                    <span className="font-semibold text-yellow-700 dark:text-yellow-300">Costo por Kg:</span>
                    <span className="font-bold text-yellow-700 dark:text-yellow-300">
                      {(() => {
                        const totalKilos = formData.settlement_details.reduce((sum, detail) => sum + (detail.kilos || 0), 0);
                        const totalNetRevenue = (formData.total_revenue_local || 0) - (formData.total_commission_local || 0) - (formData.total_tax_local || 0) - (formData.total_other_costs_local || 0);
                        const costPerKg = totalKilos > 0 ? (totalNetRevenue / (formData.exchange_rate || 1) - (formData.total_freight_usd || 0)) / totalKilos : 0;
                        return costPerKg.toFixed(4);
                      })()} USD/kg
                    </span>
                  </div> */}
                  </div>
                </div>
              </div>
            </div>
            {isSourcePreviewOpen && sourceFile && (
              <SettlementSourcePreview
                file={sourceFile}
                onClose={() => setIsSourcePreviewOpen(false)}
              />
            )}
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            variant='light'
            onPress={() => {
              onClose();
              resetForm();
            }}
            disabled={loading}
          >
            Cerrar
          </Button>
          <Button
            color='primary'
            onPress={handleSubmit}
            isLoading={loading}
            isDisabled={!!editingCostField}
          >
            Crear Liquidación
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UploadSettlementDialog;
